import * as THREE from 'three';
import { PointerLockControls } from 'three/addons/controls/PointerLockControls.js';
import { createSoundSystem } from './audio.js';
import { createWorld } from './world.js';
import { createInventoryUI } from './ui.js';
import { createPlayerController } from './player.js';
import { ITEM_DEFS, HOTBAR_DEFAULT, RECIPES, getDropItemId, getMiningSpeed, canHarvestDrop } from './game-data.js';
import { clamp01 } from './utils/math-helpers.js';
import { createMobSystem } from './mobs.js';
import { getLanguageOptions, getLanguage, setLanguage, onLanguageChange, t } from './i18n.js';
import { createSurvivalSystem } from './systems/survival-system.js';
import { createDayNightSystem } from './systems/day-night-system.js';
import { createHeldItemSystem } from './systems/held-item-system.js';
import { createBlockInteractionSystem } from './systems/block-interaction-system.js';
import { createObjectiveSystem } from './systems/objective-system.js';
import { createFurnaceSystem } from './systems/furnace-system.js';
import { createChestSystem } from './systems/chest-system.js';
import { createExperienceSystem } from './systems/experience-system.js';
import { createFarmingSystem } from './systems/farming-system.js';
import { createRedstoneSystem } from './systems/redstone-system.js';
import { createVillagerTradeSystem } from './systems/villager-trade-system.js';
import { createPlayerInteractionSystem } from './systems/player-interaction-system.js';
import { createInteractionRouterSystem } from './systems/interaction-router-system.js';
import { createPlayerCombatFluidSystem } from './systems/player-combat-fluid-system.js';
import { createWorldInteractSystem } from './systems/world-interact-system.js';
import { createRespawnSystem } from './systems/respawn-system.js';
import { createDeathFeedbackSystem } from './systems/death-feedback-system.js';
import { createPortalSystem } from './systems/portal-system.js';
import { createBoatSystem } from './systems/boat-system.js';
import { createBiomeBgmController } from './systems/bgm-controller.js';
import { createCmdSystem } from './systems/cmd-system.js';
import { createParticleEffects } from './systems/particle-effects.js';
import { createDeathRecoverySystem } from './systems/death-recovery-system.js';
import { createGameRulesStore } from './services/game-rules-store.js';
import { createSaveGameStore, GAME_SAVE_STORAGE_KEY } from './services/save-game-store.js';
import { createFrameTaskScheduler } from './services/frame-task-scheduler.js';
import { createChunkColumnWorkerClient } from './services/chunk-column-worker-client.js';
import {
  OBSIDIAN_BLOCK_ID,
  QUARTZ_BLOCK_ID,
  NETHER_PORTAL_BLOCK_ID,
  END_PORTAL_BLOCK_ID,
  PORTAL_TRAVEL_CHARGE_SECONDS,
  PORTAL_COOLDOWN_SECONDS
} from './config/game-constants.js';
import { WORLD_GEN_PROFILE } from './config/world-gen-profile.js';

const GAME_BRAND_NAME = 'Buground';
const sounds = createSoundSystem();

const GAME_BRAND_SUBTITLE = {
  'zh-TW': '致敬 Minecraft 麥塊我的世界',
  en: 'A tribute to Minecraft.'
};

const TEXTURE_STYLE_STORAGE_KEY = 'minecraft.textureStyle.v1';
const TEXTURE_STYLE_CLASSIC = 'classic';
const TEXTURE_STYLE_BUJI = 'buji';
const TEXTURE_STYLE_INDUSTRIAL = 'industrial';
const TEXTURE_STYLE_ECO = 'eco';
const WORLD_SEED_STORAGE_KEY = 'minecraft.worldSeed.v1';
const WORLD_SEED_MIN = -2147483648;
const WORLD_SEED_MAX = 2147483647;

function normalizeWorldSeed(value, fallback = 0) {
  const parsed = Number(value);
  if (Number.isFinite(parsed)) {
    return THREE.MathUtils.clamp(Math.trunc(parsed), WORLD_SEED_MIN, WORLD_SEED_MAX);
  }
  const fallbackParsed = Number(fallback);
  if (!Number.isFinite(fallbackParsed)) return 0;
  return THREE.MathUtils.clamp(Math.trunc(fallbackParsed), WORLD_SEED_MIN, WORLD_SEED_MAX);
}

function parseWorldSeedInput(value) {
  const raw = String(value ?? '').trim();
  if (!raw || !/^-?\d+$/.test(raw)) return null;
  return normalizeWorldSeed(raw, 0);
}

function readPreferredWorldSeed() {
  const defaultSeed = normalizeWorldSeed(WORLD_GEN_PROFILE.worldSeed, 0);
  try {
    const raw = localStorage.getItem(WORLD_SEED_STORAGE_KEY);
    if (raw === null) return defaultSeed;
    const parsed = parseWorldSeedInput(raw);
    return parsed === null ? defaultSeed : parsed;
  } catch {
    return defaultSeed;
  }
}

function persistPreferredWorldSeed(seed) {
  const normalized = normalizeWorldSeed(seed, 0);
  try {
    localStorage.setItem(WORLD_SEED_STORAGE_KEY, String(normalized));
    return true;
  } catch {
    return false;
  }
}

function createRandomWorldSeed(exclude = null) {
  const excluded = Number.isFinite(Number(exclude)) ? normalizeWorldSeed(exclude, 0) : null;
  for (let attempt = 0; attempt < 12; attempt += 1) {
    const next = normalizeWorldSeed((Math.random() * 0xFFFFFFFF) - 0x80000000, 0);
    if (excluded === null || next !== excluded) return next;
  }
  return normalizeWorldSeed((Date.now() & 0x7FFFFFFF), 0);
}

function normalizeTextureStyle(value) {
  const style = String(value || '').trim().toLowerCase();
  if (!style) return TEXTURE_STYLE_CLASSIC;
  if (style === TEXTURE_STYLE_CLASSIC || style === 'vanilla' || style === 'default' || style === 'none' || style === 'off') {
    return TEXTURE_STYLE_CLASSIC;
  }
  if (style === TEXTURE_STYLE_BUJI || style === 'unrestrained' || style === 'buji' || style === '不羈' || style === '不羁') {
    return TEXTURE_STYLE_BUJI;
  }
  if (style === TEXTURE_STYLE_INDUSTRIAL || style === 'minimal_industrial' || style === 'industrial_minimal' || style === '極簡工業') {
    return TEXTURE_STYLE_INDUSTRIAL;
  }
  if (style === TEXTURE_STYLE_ECO || style === 'eco_vivid' || style === 'biome' || style === 'biome_vivid' || style === '生態' || style === '繽紛') {
    return TEXTURE_STYLE_ECO;
  }
  return TEXTURE_STYLE_CLASSIC;
}

function readPreferredTextureStyle() {
  try {
    return normalizeTextureStyle(localStorage.getItem(TEXTURE_STYLE_STORAGE_KEY));
  } catch {
    return TEXTURE_STYLE_CLASSIC;
  }
}

const initialTextureStyle = readPreferredTextureStyle();
const activeWorldSeed = readPreferredWorldSeed();

const GAME_PHILOSOPHY_CONTENT = {
  'zh-TW': {
    sections: [
      'Buground，一款致敬麥塊的沙盒生存遊戲。',
      'Buground = bug + round\n地球是圓的，但這遊戲不是',
      'Buground = bug + ground\n這遊戲應該有多處未知的 bug，但因為作者太懶得修、又實在太有趣太好玩，所以通通視為 feature\n同時 bug 也是蟲蟲，地表上的蟲子（怪物）應該蠻多的',
      '所以如果有怪怪的地方，不、不是 bug，是這遊戲的特色！\n這是強調 Buground 跟 Minecraft 不同的地方！',
      '99.99% 用 AI 製作\n這遊戲對作者來說仍是一團黑箱，因為作者只玩了幾次，「沒有」經過大量完整測試，所以請隨興享受，有趣就好。'
    ],
    featuresTitle: '~~已知 bug~~ 已知 feature：',
    features: [
      '當背包滿的時候，誰說撿不起東西？\n當包包滿的時候，若還有新的掉落物會發生很酷的事情！\n就當作額外背包空間吧。看誰電腦性能好、能忍受髒亂與視野被遮擋，能享有空間就越大',
      '沒有基岩，挖穿就挖穿了\n隨時小心你的腳下方塊，它會坑你',
      '蜘蛛長得像豬？\n喔對，畢竟蜘蛛也是一種豬，而且只有四隻腳。\n...(?',
      '可以在空中朝下放方塊？\n對，你完全可以蓋空中階梯教室。\n反正無傷大雅，對吧'
    ],
    footer: '\n還有很多啦...族繁不及備載。'
  },
  en: {
    sections: [
      'Buground, a survival sandbox game tribute to Minecraft',
      'Buground = bug + round\nThe Earth is round, but this game definitely is not.',
      'Buground = bug + ground\nThis game probably has many unknown bugs, but because the author is too lazy to fix them and it is genuinely fun, they are all treated as features.\nAlso, bug means an insect, so there should be plenty of bugs and monsters crawling around on the ground.',
      'So if something feels strange, no, it is not a bug. It is one of the game\'s defining traits.\nThat is one of the clearest ways Buground sets itself apart from Minecraft.',
      '99.99% made with AI\nTo the author, this game is still a black box. They have only played it a few times and it has not gone through heavy end-to-end testing, so please enjoy it casually. If it is fun, that is enough.'
    ],
    featuresTitle: '~~Known bugs~~ Known features:',
    features: [
      'When your inventory is full, who says you cannot pick things up?\nWhen new loot appears while your bag is full, something pretty wild can happen. Treat it as bonus inventory space. The better your computer and the more chaos you can tolerate, the bigger that space becomes.',
      'There is no bedrock. If you dig through the world, you dig through the world.\nAlways watch the block under your feet.',
      'Do spiders look like pigs?\nYes. Because spiders are a kind of pig, and apparently four legs are enough.\n...(?)',
      'You can place blocks downward while mid-air?\nYes, you can totally build a floating staircase lecture hall in the sky.\nIt is harmless fun, right?'
    ],
    footer: '...and many more.'
  }
};

const bgmController = createBiomeBgmController(sounds);

const scene = new THREE.Scene();
scene.background = new THREE.Color(0x89c9ff);
scene.fog = new THREE.FogExp2(0x9fd8ff, 0.014);

const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1200);
const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
const MAX_PIXEL_RATIO = Math.min(window.devicePixelRatio, 1.25);
const MIN_PIXEL_RATIO = 0.65;
let dynamicPixelRatio = MAX_PIXEL_RATIO;
let fpsSampleFrames = 0;
let fpsSampleSeconds = 0;
renderer.setPixelRatio(dynamicPixelRatio);
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.shadowMap.enabled = false;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.05;
renderer.outputColorSpace = THREE.SRGBColorSpace;
document.body.appendChild(renderer.domElement);

const hemi = new THREE.HemisphereLight(0xcdeeff, 0x6f7f6f, 0.45);
scene.add(hemi);
const ambientLight = new THREE.AmbientLight(0xffffff, 0.24);
scene.add(ambientLight);
const dirLight = new THREE.DirectionalLight(0xfff4db, 1.0);
dirLight.position.set(35, 75, -30);
dirLight.castShadow = false;
scene.add(dirLight);

const controls = new PointerLockControls(camera, document.body);
scene.add(controls.getObject());
camera.position.y = -0.18;

const chunkColumnPrefetch = createChunkColumnWorkerClient({
  enabled: true,
  maxCacheChunks: 96,
  maxInFlight: 3,
  worldSeed: activeWorldSeed
});

const world = createWorld(scene, renderer, {
  columnPrefetch: chunkColumnPrefetch,
  onCactusConsumeDrop: handleCactusConsumeDrop,
  textureStyle: initialTextureStyle,
  worldSeed: activeWorldSeed
});
world.generateTerrain({ x: 0, z: 0 });

const frameTaskScheduler = createFrameTaskScheduler({
  defaultBudgetMs: 2.1
});

let objectiveSystem = null;
let furnaceSystem = null;
let chestSystem = null;
let experienceSystem = null;
let farmingSystem = null;
let redstoneSystem = null;
let villagerTradeSystem = null;
let respawnSystem = null;
let interactionRouterSystem = null;
let deathFeedbackSystem = null;
let boatSystem = null;
let cmdSystem = null;

const ui = createInventoryUI({
  itemDefs: ITEM_DEFS,
  recipes: RECIPES,
  hotbarDefault: HOTBAR_DEFAULT,
  onQuickMoveInventorySlot: ({ itemId, count, durability }) => (
    chestSystem?.quickMoveFromInventory?.({ itemId, count, durability })
    || furnaceSystem?.quickMoveFromInventory?.({ itemId, count, durability })
    || 0
  ),
  onCollectSimilarExternal: ({ itemId, maxTake }) => (
    chestSystem?.collectSimilarToInventoryCursor?.({ itemId, maxTake }) || 0
  ),
  onDropCursorOutside: (payload) => handleInventoryCursorWorldDrop(payload),
  onCraft: (_recipeId, outItemId, outCount) => {
    sounds.craft();
    objectiveSystem?.onCraftItem(outItemId, outCount);
  }
});

const DEV_STARTER_KIT = false;
const gameRules = createGameRulesStore();
gameRules.read();
const saveGameStore = createSaveGameStore({
  storageKey: `${GAME_SAVE_STORAGE_KEY}_seed_${activeWorldSeed}`
});

function shouldKeepInventoryOnDeath() {
  return gameRules.isKeepInventoryOnDeath();
}

function isCreativeMode() {
  return gameRules.isCreativeMode();
}

function isCoordinatesShown() {
  return gameRules.isShowCoordinates();
}
if (DEV_STARTER_KIT) {
  ui.addItem('3', 8);
  ui.addItem('2', 16);
  ui.addItem('1', 16);
  ui.addItem('wood_pickaxe', 1);
  ui.addItem('wood_axe', 1);
  ui.addItem('wood_shovel', 1);
  ui.addItem('wood_sword', 1);
  ui.addItem('shield', 1);
  ui.addItem('bow', 1);
  ui.addItem('arrow', 28);
  ui.addItem('iron_helmet', 1);
  ui.addItem('iron_chestplate', 1);
  ui.addItem('11', 1);
  ui.addItem('13', 1);
  ui.addItem('14', 1);
  ui.addItem('16', 3);
  ui.addItem('18', 4);
  ui.addItem('20', 6);
  ui.addItem('31', 14);
  ui.addItem('coal', 8);
  ui.addItem('diamond_pickaxe', 1);
  ui.addItem('flint_steel', 1);
  ui.addItem('water_bucket', 1);
  ui.addItem('beef_raw', 6);
  ui.assignItemToHotbar('beef_raw');
}
ui.updateInventoryUI();

const BLOCKED_CTRL_SHORTCUTS = new Set(['KeyD', 'KeyS', 'KeyW', 'KeyR']);
const HOTBAR_DIGIT_RE = /^Digit([1-9])$/;
const HOTBAR_NUMPAD_RE = /^Numpad([1-9])$/;
const keyboardLockApi = (navigator.keyboard && typeof navigator.keyboard.lock === 'function' && typeof navigator.keyboard.unlock === 'function')
  ? navigator.keyboard
  : null;
let hasKeyboardLock = false;
let imeComposing = false;

function isUiWindowOpen() {
  return ui.isCraftOpen()
    || !!furnaceSystem?.isOpen()
    || !!chestSystem?.isOpen()
    || !!villagerTradeSystem?.isOpen()
    || !!cmdSystem?.isOpen();
}

function isEditableKeyboardTarget(target) {
  if (!(target instanceof Element)) return false;
  const editable = target.closest('input, textarea, select, [contenteditable], [role="textbox"]');
  if (!editable) return false;
  if (editable instanceof HTMLInputElement
    || editable instanceof HTMLTextAreaElement
    || editable instanceof HTMLSelectElement) {
    return !editable.disabled;
  }
  return true;
}

function isImeComposingKeyEvent(e) {
  if (!e) return false;
  const key = typeof e.key === 'string' ? e.key : '';
  return imeComposing || e.isComposing || key === 'Process' || key === 'Dead' || Number(e.keyCode) === 229;
}

function shouldIgnoreGameKeyboardEvent(e) {
  return isEditableKeyboardTarget(e.target) || isImeComposingKeyEvent(e);
}

function getHotbarSlotFromKeyEvent(e) {
  const code = typeof e.code === 'string' ? e.code : '';
  let match = HOTBAR_DIGIT_RE.exec(code);
  if (match) return Number(match[1]);
  match = HOTBAR_NUMPAD_RE.exec(code);
  if (match) return Number(match[1]);

  const key = Number.parseInt(e.key, 10);
  if (Number.isNaN(key) || key < 1 || key > ui.getHotbarSize()) return null;
  return key;
}

function shouldBlockBrowserShortcut(e) {
  if (!controls.isLocked) return false;
  if (isUiWindowOpen()) return false;
  if (shouldIgnoreGameKeyboardEvent(e)) return false;
  if (!(e.ctrlKey || e.metaKey)) return false;
  return BLOCKED_CTRL_SHORTCUTS.has(e.code);
}

async function tryEnableKeyboardLock() {
  if (!keyboardLockApi) return;
  try {
    await keyboardLockApi.lock(['ControlLeft', 'ControlRight', 'AltLeft', 'AltRight', 'MetaLeft', 'MetaRight', 'KeyW', 'KeyA', 'KeyS', 'KeyD', 'KeyR']);
    hasKeyboardLock = true;
  } catch {
    hasKeyboardLock = false;
  }
}

function releaseKeyboardLock() {
  if (!keyboardLockApi || !hasKeyboardLock) return;
  keyboardLockApi.unlock();
  hasKeyboardLock = false;
}

function showStatusToast(text, type = '', duration = 2.2) {
  if (!statusToastEl || !text) return;
  if (statusToastType) statusToastEl.classList.remove(statusToastType);
  statusToastType = type || '';
  if (statusToastType) statusToastEl.classList.add(statusToastType);
  statusToastEl.textContent = text;
  statusToastEl.classList.add('show');
  statusToastTimer = Math.max(0.25, duration);
}

function showStatusByKey(key, vars = {}, type = '', duration = 2.2) {
  showStatusToast(t(key, vars), type, duration);
}

function updateStatusToast(delta) {
  if (!statusToastEl || statusToastTimer <= 0) return;
  statusToastTimer = Math.max(0, statusToastTimer - Math.max(0, delta));
  if (statusToastTimer > 0) return;
  statusToastEl.classList.remove('show');
  if (statusToastType) statusToastEl.classList.remove(statusToastType);
  statusToastType = '';
}

let pauseMenuVisible = true;
let nextUnlockBehavior = 'auto';
let philosophyPanelOpen = false;

const instructionsTitleEl = document.getElementById('instructions-title');
const instructionsSubtitleEl = document.getElementById('instructions-subtitle');
const instructionLine1El = document.getElementById('instruction-line-1');
const instructionLine2El = document.getElementById('instruction-line-2');
const instructionLine3El = document.getElementById('instruction-line-3');
const instructionLine4El = document.getElementById('instruction-line-4');
const instructionLine5El = document.getElementById('instruction-line-5');
const startBtnEl = document.getElementById('start-btn');
const instructionsEl = document.getElementById('instructions');
const menuRulesTitleEl = document.getElementById('menu-rules-title');
const keepInventoryToggleEl = document.getElementById('rule-keep-inventory-toggle');
const keepInventoryLabelEl = document.getElementById('rule-keep-inventory-label');
const creativeModeToggleEl = document.getElementById('rule-creative-toggle');
const creativeModeLabelEl = document.getElementById('rule-creative-label');
const coordinatesToggleEl = document.getElementById('rule-coordinates-toggle');
const coordinatesLabelEl = document.getElementById('rule-coordinates-label');
const bgmDebugToggleEl = document.getElementById('rule-bgm-debug-toggle');
const bgmDebugLabelEl = document.getElementById('rule-bgm-debug-label');
const menuAudioTitleEl = document.getElementById('menu-audio-title');
const viewDistanceLabelEl = document.getElementById('view-distance-label');
const viewDistanceSliderEl = document.getElementById('view-distance-slider');
const viewDistanceValueEl = document.getElementById('view-distance-value');
const textureStyleLabelEl = document.getElementById('texture-style-label');
const textureStyleSelectEl = document.getElementById('texture-style-select');
const musicVolumeLabelEl = document.getElementById('music-volume-label');
const musicVolumeSliderEl = document.getElementById('music-volume-slider');
const musicVolumeValueEl = document.getElementById('music-volume-value');
const sfxVolumeLabelEl = document.getElementById('sfx-volume-label');
const sfxVolumeSliderEl = document.getElementById('sfx-volume-slider');
const sfxVolumeValueEl = document.getElementById('sfx-volume-value');
const menuSeedTitleEl = document.getElementById('menu-seed-title');
const worldSeedLabelEl = document.getElementById('world-seed-label');
const worldSeedInputEl = document.getElementById('world-seed-input');
const worldSeedApplyBtnEl = document.getElementById('world-seed-apply-btn');
const worldSeedRandomBtnEl = document.getElementById('world-seed-random-btn');
const worldSeedHintEl = document.getElementById('menu-seed-hint');
const menuSaveTitleEl = document.getElementById('menu-save-title');
const saveGameBtnEl = document.getElementById('save-game-btn');
const loadGameBtnEl = document.getElementById('load-game-btn');
const deleteSaveBtnEl = document.getElementById('delete-save-btn');
const saveMetaEl = document.getElementById('menu-save-meta');
const philosophyBtnEl = document.getElementById('philosophy-btn');
const philosophyScrimEl = document.getElementById('philosophy-scrim');
const philosophyPanelEl = document.getElementById('philosophy-panel');
const philosophyTitleEl = document.getElementById('philosophy-title');
const philosophyContentEl = document.getElementById('philosophy-content');
const philosophyCloseBtnEl = document.getElementById('philosophy-close-btn');

const timeLabelEl = document.getElementById('time-label');
const languageSelectEl = document.getElementById('language-select');
const langLabelEl = document.getElementById('lang-label');
const statusToastEl = document.getElementById('status-toast');
const hurtOverlayEl = document.getElementById('hurt-overlay');
const damageDirectionIndicatorEl = document.getElementById('damage-direction-indicator');
const deathOverlayEl = document.getElementById('death-overlay');
const deathTextEl = document.getElementById('death-text');
const deathReasonEl = document.getElementById('death-reason');
const fireOverlayEl = document.getElementById('fire-overlay');
const coordsHudLabelEl = document.getElementById('coords-label');
const recoveryLabelEl = document.getElementById('recovery-label');
const bgmDebugEl = document.getElementById('bgm-debug');
const BGM_DEBUG_VISIBILITY_KEY = 'minecraft.bgmDebugVisible';
const AUDIO_SETTINGS_STORAGE_KEY = 'minecraft.audioSettings.v1';
const VIEW_DISTANCE_SETTINGS_STORAGE_KEY = 'minecraft.viewDistance.v1';
const PERF_HUD_VISIBILITY_KEY = 'minecraft.perfHudVisible';

let isMining = false;
let miningTarget = null;
let miningProgress = 0;
let miningAimLostTimer = 0;
let creativeHoldMineCooldown = 0;
let lastHitSoundTime = 0;
let meleeCooldown = 0;
let rangedCooldown = 0;
let bowCharging = false;
let bowChargeStartTime = 0;
let isBlockingWithShield = false;
let tableAccessRefreshTimer = 0;
let statusToastTimer = 0;
let statusToastType = '';
let portalChargeTime = 0;
let portalCooldownTime = 0;

let bgmSampleTimer = 0;
let coordsHudSampleTimer = 0;
let bgmDebugHudSampleTimer = 0;
let bgmDebugVisible = false;
let perfHudVisible = false;
let perfHudSampleTimer = 0;
let performanceTier = 1;
let performanceTierSampleTimer = 0;
let performanceTierCandidate = performanceTier;
let performanceTierCandidateStreak = 0;
let mobUpdateAccumulator = 0;
let fluidUpdateAccumulator = 0;
let dropUpdateAccumulator = 0;
let interactionRefreshAccumulator = 0;
let musicVolume = 0.68;
let sfxVolume = 0.85;
let preferredViewDistance = 4;
let preferredTextureStyle = initialTextureStyle;
let bgmContext = {
  dimension: 'overworld',
  biome: null,
  threat: 0
};
const CACTUS_BLOCK_ID = 72;
const CACTUS_TOUCH_SAMPLE_RADIUS = 0.35;
const PLAYER_DISCARD_DROP_SOURCE = 'player_discard';

try {
  bgmDebugVisible = localStorage.getItem(BGM_DEBUG_VISIBILITY_KEY) === '1';
} catch {
  bgmDebugVisible = false;
}
try {
  perfHudVisible = localStorage.getItem(PERF_HUD_VISIBILITY_KEY) === '1';
} catch {
  perfHudVisible = false;
}
try {
  const rawAudioSettings = localStorage.getItem(AUDIO_SETTINGS_STORAGE_KEY);
  if (rawAudioSettings) {
    const parsedAudioSettings = JSON.parse(rawAudioSettings);
    if (Number.isFinite(Number(parsedAudioSettings?.musicVolume))) {
      musicVolume = clamp01(Number(parsedAudioSettings.musicVolume));
    }
    if (Number.isFinite(Number(parsedAudioSettings?.sfxVolume))) {
      sfxVolume = clamp01(Number(parsedAudioSettings.sfxVolume));
    }
  }
} catch {
  // Ignore invalid audio settings payload.
}
try {
  const rawViewDistance = localStorage.getItem(VIEW_DISTANCE_SETTINGS_STORAGE_KEY);
  if (rawViewDistance !== null) {
    const parsed = Number(rawViewDistance);
    if (Number.isFinite(parsed)) {
      preferredViewDistance = THREE.MathUtils.clamp(Math.floor(parsed), 1, 8);
    }
  }
} catch {
  // Ignore invalid view-distance settings payload.
}
if (bgmDebugEl) {
  bgmDebugEl.style.display = bgmDebugVisible ? 'block' : 'none';
}
bgmController.setMasterVolume(musicVolume);
sounds.setMasterVolume?.(sfxVolume);

function createPerfHudElement() {
  const el = document.createElement('div');
  el.id = 'perf-hud';
  el.style.cssText = [
    'position:absolute',
    'right:14px',
    'top:188px',
    'z-index:8',
    'min-width:260px',
    'max-width:min(460px, calc(100vw - 28px))',
    'padding:8px 10px',
    'border-radius:8px',
    'border:1px solid rgba(109,255,187,0.44)',
    'background:rgba(8,22,16,0.84)',
    'color:#d4ffe8',
    'font-size:11px',
    'line-height:1.35',
    "font-family:'JetBrains Mono','Fira Code','Consolas',monospace",
    'white-space:pre-line',
    'box-shadow:0 8px 20px rgba(0,0,0,0.28)',
    'pointer-events:none',
    'text-shadow:0 1px 1px rgba(0,0,0,0.38)',
    'display:none'
  ].join(';');
  document.body.appendChild(el);
  return el;
}

const perfHudEl = createPerfHudElement();
const perfStats = {
  frame: [],
  render: [],
  update: [],
  streaming: [],
  mobs: [],
  fluids: [],
  interaction: []
};
const PERFORMANCE_TIER_LABELS = ['Protect', 'Balanced', 'Turbo'];

const quickDropDir = new THREE.Vector3();
const quickDropSpawnPos = new THREE.Vector3();
const miningLookDir = new THREE.Vector3();
const QUICK_DROP_FORWARD_OFFSET = 0.46;
const QUICK_DROP_VERTICAL_OFFSET = -0.02;
const QUICK_DROP_FORWARD_SPEED = 4.2;
const QUICK_DROP_UPWARD_SPEED = 2.15;
const QUICK_DROP_EXTRA_DISTANCE_BLOCKS = 2.5;
const QUICK_DROP_PICKUP_COOLDOWN = 1.22;
const CREATIVE_HOLD_MINE_COOLDOWN = 0.06;

const miningOverlayMat = new THREE.MeshBasicMaterial({
  color: 0x111111,
  wireframe: true,
  transparent: true,
  opacity: 0,
  depthWrite: false,
  depthTest: false
});
const miningOverlayMesh = new THREE.Mesh(new THREE.BoxGeometry(1.03, 1.03, 1.03), miningOverlayMat);
miningOverlayMesh.visible = false;
miningOverlayMesh.renderOrder = 1100;
scene.add(miningOverlayMesh);

let miningPulse = 0;
let miningRequiredTime = 0.12;

function updateMineUI(percentage) {
  const container = document.getElementById('mine-progress-container');
  if (!container) return;

  if (percentage > 0) {
    container.style.display = 'block';
    const bar = document.getElementById('mine-progress-bar');
    if (bar) bar.style.width = `${Math.min(100, percentage)}%`;
  } else {
    container.style.display = 'none';
  }
}


function updateMiningOverlay(delta = 0.016) {
  miningPulse = Math.max(0, miningPulse - delta * 3.8);

  if (!isMining || !miningTarget || !world.hasRaycastObject(miningTarget)) {
    miningOverlayMesh.visible = false;
    miningOverlayMat.opacity = 0;
    return;
  }

  const pct = THREE.MathUtils.clamp(miningProgress / Math.max(0.01, miningRequiredTime), 0, 1);
  const pulse = miningPulse * 0.18;
  miningOverlayMesh.visible = true;
  miningOverlayMesh.position.copy(miningTarget.position);
  miningOverlayMesh.scale.setScalar(1.015 + pct * 0.04 + pulse);
  miningOverlayMat.opacity = 0.05 + pct * 0.46 + pulse * 0.75;
}

function stopMining() {
  isMining = false;
  miningTarget = null;
  miningProgress = 0;
  miningAimLostTimer = 0;
  miningRequiredTime = 0.12;
  miningPulse = 0;
  miningOverlayMesh.visible = false;
  miningOverlayMat.opacity = 0;
  updateMineUI(0);
}

function getBlockAimKey(block) {
  if (!block?.position) return null;
  if (block.userData?.blockKey !== undefined && block.userData?.blockKey !== null) {
    return `k:${block.userData.blockKey}`;
  }
  const x = Math.round(block.position.x);
  const y = Math.round(block.position.y);
  const z = Math.round(block.position.z);
  return `p:${x},${y},${z}`;
}

function isSameBlockTarget(a, b) {
  if (!a || !b) return false;
  if (a === b) return true;
  return getBlockAimKey(a) === getBlockAimKey(b);
}

function cancelBowCharge() {
  bowCharging = false;
  bowChargeStartTime = 0;
}

function stopShieldBlocking() {
  isBlockingWithShield = false;
}

function isPlayerDeathAnimating() {
  return !!deathFeedbackSystem?.isDeathAnimating?.();
}

deathFeedbackSystem = createDeathFeedbackSystem({
  camera,
  controls,
  t,
  hurtOverlayEl,
  damageDirectionIndicatorEl,
  deathOverlayEl,
  deathTextEl,
  deathReasonEl,
  onBeforeDeathAnimationStart: () => {
    stopMining();
    cancelBowCharge();
    stopShieldBlocking();
    player.resetInput();
    boatSystem?.dismountMountedBoat?.({ skipReposition: true, silent: true });
    ui.setCraftOpen(false);
    if (furnaceSystem?.isOpen()) furnaceSystem.close();
    if (chestSystem?.isOpen()) chestSystem.close();
    if (villagerTradeSystem?.isOpen()) villagerTradeSystem.close();
  },
  onDeathAnimationComplete: () => {
    respawnPlayer();
  }
});
deathFeedbackSystem.refreshLocale?.();

const dayNight = createDayNightSystem({
  scene,
  hemi,
  ambientLight,
  dirLight,
  timeLabelEl,
  t,
  dayLengthSeconds: 720
});

experienceSystem = createExperienceSystem({
  t,
  onLevelUp: (level) => {
    showStatusByKey('status.level_up', { level }, 'level', 2.5);
  }
});

let survival = createSurvivalSystem({
  ui,
  itemDefs: ITEM_DEFS,
  sounds,
  t,
  isBlocking: () => (
    controls.isLocked &&
    !isUiWindowOpen() &&
    ui.getActiveItemId() === 'shield' &&
    ui.getActiveItemCount() > 0 &&
    isBlockingWithShield
  ),
  onBlockDamage: () => {
    if (!isCreativeMode()) ui.damageActiveTool(1);
    if (sounds.equip) sounds.equip();
  },
  onDamageFeedback: ({ type, amount, sourcePosition }) => {
    deathFeedbackSystem?.triggerDamageFeedback(type, sourcePosition, amount);
    playerCombatFluidSystem?.triggerDamageFovPunch?.();
  },
  onConsumeBlocked: (reason) => {
    if (reason === 'health_full') {
      showStatusByKey('status.consume_health_full', {}, 'level', 1.5);
      return;
    }
    if (reason === 'hunger_full') {
      showStatusByKey('status.consume_hunger_full', {}, 'level', 1.5);
    }
  },
  creativeMode: isCreativeMode(),
  onPlayerDeath: ({ type, sourcePosition } = {}) => {
    deathFeedbackSystem?.startDeathAnimation(type, sourcePosition);
  }
});

objectiveSystem = createObjectiveSystem({
  ui,
  dayNight,
  itemDefs: ITEM_DEFS,
  t
});

furnaceSystem = createFurnaceSystem({
  world,
  ui,
  itemDefs: ITEM_DEFS,
  t,
  onRequestClose: () => closeAllWindowsAndResume(),
  onTakeOutput: ({ itemId, count }) => {
    experienceSystem?.onTakeSmeltOutput(itemId, count);
  }
});

chestSystem = createChestSystem({
  world,
  ui,
  itemDefs: ITEM_DEFS,
  t,
  onRequestClose: () => closeAllWindowsAndResume()
});

villagerTradeSystem = createVillagerTradeSystem({
  ui,
  itemDefs: ITEM_DEFS,
  t,
  onRequestClose: () => closeAllWindowsAndResume()
});

const heldItem = createHeldItemSystem({
  camera,
  controls,
  ui,
  itemDefs: ITEM_DEFS
});

const player = createPlayerController({
  camera,
  controls,
  world,
  onStep: (surfaceId, inWater, sprinting) => {
    sounds.stepBySurface(surfaceId, inWater, sprinting);
  },
  onFlightToggle: (enabled) => {
    showStatusByKey(enabled ? 'status.fly_on' : 'status.fly_off', {}, 'level', 1.6);
  },
  onFallDamage: (damage, damageType = 'fall', fallDistance = 0) => {
    if (damageType === 'fall') {
      particles.spawnLandingDebris(fallDistance);
    }
    if (isCreativeMode() && damageType === 'void') {
      controls.getObject().position.copy(respawnSystem?.resolveRespawnPosition?.() || getWorldSpawnPosition());
      player.resetInput();
      stopMining();
      cancelBowCharge();
      stopShieldBlocking();
      boatSystem?.dismountMountedBoat?.({ skipReposition: true, silent: true });
      return;
    }
    survival.applyDamage(damage, damageType);
  }
});
player.setCreativeMode(isCreativeMode());
ui.setCreativeMode(isCreativeMode());
refreshBgmContext(true);

const particles = createParticleEffects({ scene, world, player, CACTUS_BLOCK_ID });

const blockInteraction = createBlockInteractionSystem({
  scene,
  camera,
  controls,
  ui,
  world,
  player,
  itemDefs: ITEM_DEFS
});

boatSystem = createBoatSystem({
  scene,
  camera,
  controls,
  world,
  player,
  ui,
  itemDefs: ITEM_DEFS,
  isCreativeMode,
  sounds,
  heldItem,
  findSafeStandPosition: findSafeRespawnNear
});

function spawnItemDrop(x, y, z, itemId, count = 1, opts = {}) {
  const def = ITEM_DEFS[itemId];
  world.spawnDrop(x, y, z, itemId, {
    kind: 'item',
    color: def?.color,
    count,
    ...opts
  });
}

const PASSIVE_XP_MOB_TYPES = new Set([
  'cow',
  'sheep',
  'pig',
  'chicken',
  'goat',
  'duck',
  'villager'
]);

const XP_ORB_VISUAL_VARIANTS = Object.freeze({
  tiny: Object.freeze([
    { color: 0x61d865, scale: 0.56 },
    { color: 0x72ea67, scale: 0.6 },
    { color: 0x57c95b, scale: 0.53 }
  ]),
  small: Object.freeze([
    { color: 0x83ef6b, scale: 0.68 },
    { color: 0x96fb74, scale: 0.73 },
    { color: 0x76dd63, scale: 0.64 }
  ]),
  medium: Object.freeze([
    { color: 0xb6ff7f, scale: 0.82 },
    { color: 0xc8ff8b, scale: 0.88 },
    { color: 0xa2f06f, scale: 0.78 }
  ]),
  large: Object.freeze([
    { color: 0xebffab, scale: 0.96 },
    { color: 0xf8ffd1, scale: 1.04 },
    { color: 0xd8ff8f, scale: 0.9 }
  ])
});

function resolveExperienceOrbCount(totalXp, { mobType = '', isKillBonus = false } = {}) {
  const xp = Math.max(0, Number(totalXp) || 0);
  const type = String(mobType || '');
  const isPassive = PASSIVE_XP_MOB_TYPES.has(type);
  const isHostile = type.length > 0 && !isPassive;

  let target = xp >= 12 ? 10 : xp >= 7 ? 8 : xp >= 3 ? 6 : xp >= 1.2 ? 4 : 2;
  if (isKillBonus && isHostile) target += 1;
  else if (isPassive && xp < 2.5) target = Math.max(1, target - 1);
  return Math.max(1, Math.min(12, Math.round(target)));
}

function resolveExperienceOrbVariant(totalXp, { mobType = '', isKillBonus = false } = {}, orbIndex = 0) {
  const xp = Math.max(0, Number(totalXp) || 0);
  const type = String(mobType || '');
  const isPassive = PASSIVE_XP_MOB_TYPES.has(type);
  const isHostile = type.length > 0 && !isPassive;

  let tier = 'tiny';
  if (xp >= 9) tier = 'large';
  else if (xp >= 4) tier = 'medium';
  else if (xp >= 1.5) tier = 'small';

  if (isKillBonus && isHostile) {
    tier = tier === 'tiny' ? 'small' : (tier === 'small' ? 'medium' : 'large');
  } else if (isPassive && tier !== 'tiny') {
    tier = tier === 'large' ? 'medium' : (tier === 'medium' ? 'small' : 'tiny');
  }

  const pool = XP_ORB_VISUAL_VARIANTS[tier] || XP_ORB_VISUAL_VARIANTS.small;
  const jitter = Math.floor(Math.random() * pool.length);
  return pool[(orbIndex + jitter) % pool.length];
}

function spawnExperienceDrops(x, y, z, totalXp, context = {}) {
  const xp = Math.max(0, Number(totalXp) || 0);
  if (xp <= 0.0001) return;

  const orbCount = resolveExperienceOrbCount(xp, context);
  const valuePerOrb = xp / orbCount;
  for (let i = 0; i < orbCount; i++) {
    const visual = resolveExperienceOrbVariant(xp, context, i);
    world.spawnDrop(x, y, z, valuePerOrb, {
      kind: 'xp',
      color: visual.color,
      scale: visual.scale,
      count: 1,
      spawnSpreadHorizontal: 0.09,
      spawnSpreadVertical: 0.14
    });
  }
}

farmingSystem = createFarmingSystem({
  world,
  ui,
  itemDefs: ITEM_DEFS,
  spawnItemDrop,
  onUseHoe: () => {
    if (!isCreativeMode()) ui.damageActiveTool(1);
    if (sounds.equip) sounds.equip();
  }
});

redstoneSystem = createRedstoneSystem({
  world,
  spawnItemDrop
});

frameTaskScheduler.registerTask('objective-system', {
  minInterval: 0.05,
  maxInterval: 0.18,
  priority: 4,
  run: ({ elapsed }) => {
    objectiveSystem?.update(Math.max(0.016, elapsed));
  }
});

frameTaskScheduler.registerTask('furnace-system', {
  minInterval: 0.05,
  maxInterval: 0.14,
  priority: 5,
  run: ({ elapsed }) => {
    furnaceSystem?.update(Math.max(0.016, elapsed));
  }
});

frameTaskScheduler.registerTask('chest-system', {
  minInterval: 0.08,
  maxInterval: 0.2,
  priority: 2,
  run: ({ elapsed }) => {
    chestSystem?.update(Math.max(0.016, elapsed));
  }
});

frameTaskScheduler.registerTask('farming-system', {
  minInterval: 0.07,
  maxInterval: 0.22,
  priority: 3,
  run: ({ elapsed }) => {
    farmingSystem?.update(Math.max(0.016, elapsed));
  }
});

frameTaskScheduler.registerTask('villager-trade-system', {
  minInterval: 0.16,
  maxInterval: 0.35,
  priority: 1,
  run: () => {
    villagerTradeSystem?.update(dayNight.getDayCount());
  }
});

frameTaskScheduler.registerTask('light-budget', {
  minInterval: 0.3,
  maxInterval: 0.8,
  priority: 2,
  run: () => {
    world.refreshLightBudget(player.getPosition());
  }
});

function dropPlayerInventoryAt(position, { source = '' } = {}) {
  if (shouldKeepInventoryOnDeath() || !position || typeof ui.dropAllItems !== 'function') return 0;

  return ui.dropAllItems(({ itemId, count }) => {
    spawnItemDrop(
      position.x + (Math.random() * 0.8 - 0.4),
      position.y - 0.72 + Math.random() * 0.36,
      position.z + (Math.random() * 0.8 - 0.4),
      itemId,
      count,
      source ? { source } : {}
    );
  });
}

function dropActiveItemFromPlayer({ count = 1 } = {}) {
  if (!controls.isLocked || isUiWindowOpen() || typeof ui.dropActiveItem !== 'function') return false;

  const dropped = ui.dropActiveItem(count);
  if (!dropped?.itemId || dropped.count <= 0) return false;

  return spawnPlayerDiscardDrop(dropped) > 0;
}

function spawnPlayerDiscardDrop({ itemId = null, count = 1 } = {}) {
  if (!itemId) return 0;
  const dropCount = Math.max(1, Math.floor(Number(count) || 1));
  camera.getWorldDirection(quickDropDir);
  if (quickDropDir.lengthSq() < 0.0001) quickDropDir.set(0, 0, -1);
  else quickDropDir.normalize();
  const lookPitch = THREE.MathUtils.clamp(quickDropDir.y, -0.45, 0.72);
  const horizontalLen = Math.hypot(quickDropDir.x, quickDropDir.z);
  if (horizontalLen < 0.0001) {
    quickDropDir.set(0, 0, -1);
  } else {
    quickDropDir.x /= horizontalLen;
    quickDropDir.z /= horizontalLen;
    quickDropDir.y = 0;
  }
  const throwLift = THREE.MathUtils.clamp(0.24 + lookPitch * 0.48, 0.12, 0.58);
  const throwSpeedScale = 1 + throwLift * 0.55;
  const approxFlightTime = 0.56 + throwLift * 0.1;
  const forwardSpeed = (QUICK_DROP_FORWARD_SPEED * throwSpeedScale)
    + (QUICK_DROP_EXTRA_DISTANCE_BLOCKS / Math.max(0.2, approxFlightTime));

  camera.getWorldPosition(quickDropSpawnPos);
  const spawnX = quickDropSpawnPos.x + (quickDropDir.x * QUICK_DROP_FORWARD_OFFSET);
  const spawnY = quickDropSpawnPos.y + QUICK_DROP_VERTICAL_OFFSET + throwLift * 0.08;
  const spawnZ = quickDropSpawnPos.z + (quickDropDir.z * QUICK_DROP_FORWARD_OFFSET);
  const def = ITEM_DEFS[itemId];
  world.spawnDrop(spawnX, spawnY, spawnZ, itemId, {
    kind: 'item',
    color: def?.color,
    count: dropCount,
    source: PLAYER_DISCARD_DROP_SOURCE,
    pickupCooldown: QUICK_DROP_PICKUP_COOLDOWN,
    vx: quickDropDir.x * forwardSpeed,
    vy: QUICK_DROP_UPWARD_SPEED + throwLift * 1.55,
    vz: quickDropDir.z * forwardSpeed,
    spawnSpreadHorizontal: 0.03,
    spawnSpreadVertical: 0.05
  });
  return dropCount;
}

function handleInventoryCursorWorldDrop({ itemId = null, count = 0 } = {}) {
  if (!itemId || count <= 0) return 0;
  return spawnPlayerDiscardDrop({ itemId, count });
}

function handleCactusConsumeDrop({ x, y, z, rootX, rootY, rootZ, height = 1 } = {}) {
  if (![x, y, z, rootX, rootY, rootZ].every((value) => Number.isFinite(Number(value)))) return;
  const safeHeight = Math.max(1, Math.floor(Number(height) || 1));
  const targetY = rootY + Math.min(safeHeight - 0.18, 3.18);
  particles.spawnCactusAbsorbShellPulse(rootX, rootY, rootZ, safeHeight);
  particles.spawnCactusConsumeParticles(x, y, z, {
    targetX: rootX,
    targetY,
    targetZ: rootZ,
    count: 5 + Math.floor(Math.random() * 3)
  });
  particles.spawnCactusConsumePulse(rootX, targetY, rootZ, 4 + Math.floor(Math.random() * 2));
}

function getCurrentDimension() {
  return world.getDimensionAt(player.getPosition().x, player.getPosition().z);
}

function refreshBgmContext(force = false, dimensionOverride = null) {
  if (!force && bgmSampleTimer > 0) return;

  const pos = player.getPosition();
  const dimension = dimensionOverride || world.getDimensionAt(pos.x, pos.z);
  if (dimension === 'overworld') {
    const ecosystem = world.getEcosystemAt(pos.x, pos.z);
    bgmContext = {
      dimension,
      biome: ecosystem?.biome || null,
      threat: Number(ecosystem?.threat || 0)
    };
  } else {
    bgmContext = {
      dimension,
      biome: null,
      threat: 0
    };
  }

  bgmSampleTimer = 2.4;
}

let portalSystem = null;

function tryIgnitePortalFromHitFace(hit) {
  return !!portalSystem?.tryIgnitePortalFromHitFace?.(hit);
}

function tryIgnitePortalFromObsidianBlock(block) {
  return !!portalSystem?.tryIgnitePortalFromObsidianBlock?.(block);
}

function tryIgniteFireFromHitFace(hit) {
  return !!portalSystem?.tryIgniteFireFromHitFace?.(hit);
}

function tryActivateEndPortalFromQuartzBlock(block) {
  return !!portalSystem?.tryActivateEndPortalFromQuartzBlock?.(block);
}

function cleanupBrokenPortalBlocksNear(x, y, z) {
  portalSystem?.cleanupBrokenPortalBlocksNear?.(x, y, z);
}

function getPlayerPortalBlockId() {
  return portalSystem?.getPlayerPortalBlockId?.() ?? null;
}

function tryTravelThroughNetherPortal() {
  const traveled = !!portalSystem?.tryTravelThroughNetherPortal?.();
  if (traveled) {
    portalChargeTime = 0;
    portalCooldownTime = PORTAL_COOLDOWN_SECONDS;
  }
  return traveled;
}

function tryTravelThroughEndPortal() {
  const traveled = !!portalSystem?.tryTravelThroughEndPortal?.();
  if (traveled) {
    portalChargeTime = 0;
    portalCooldownTime = PORTAL_COOLDOWN_SECONDS;
  }
  return traveled;
}

function getWorldSpawnPosition() {
  const standHeight = Math.max(1.8, player.getHeight());
  return new THREE.Vector3(0, world.getSurfaceHeight(0, 0) + standHeight + 2.4, 0);
}

respawnSystem = createRespawnSystem({
  world,
  dayNight,
  t,
  onBedExplode,
  showStatusByKey,
  getWorldSpawnPosition,
  findSafeRespawnNear
});
controls.getObject().position.copy(getWorldSpawnPosition());

const deathRecovery = createDeathRecoverySystem({ scene, world, player, t, showStatusByKey, recoveryLabelEl });

const mobSystem = createMobSystem({
  scene,
  camera,
  world,
  player,
  t,
  onDamagePlayer: (amount, type, sourcePosition = null) => survival.applyDamage(amount, type, { sourcePosition }),
  onCreeperExplode: (pos) => onCreeperExplode(pos),
  onMobSound: ({ event, type, mode = 'melee', distance = 0, progress = 0, urgent = false }) => {
    if (event === 'ambient') {
      sounds.mobAmbient?.(type, distance);
      return;
    }
    if (event === 'attack') {
      sounds.mobAttack?.(type, mode, distance);
      return;
    }
    if (event === 'creeper_fuse') {
      sounds.creeperFuse?.(progress, urgent, distance);
      return;
    }
    if (event === 'explode') {
      sounds.mobAttack?.(type, 'melee', distance);
    }
  },
  onMobDeath: ({ type, cause, position }) => {
    sounds.mobDeath(type, cause);
    objectiveSystem?.onMobKilled(type, cause);
    if (cause === 'player') {
      const bonusXp = experienceSystem?.getMobKillBonusExperience?.(type, cause) || 0;
      if (bonusXp > 0) {
        const px = Number(position?.x);
        const py = Number(position?.y);
        const pz = Number(position?.z);
        const fallback = player.getPosition();
        spawnExperienceDrops(
          Number.isFinite(px) ? px : fallback.x,
          (Number.isFinite(py) ? py : fallback.y) + 0.2,
          Number.isFinite(pz) ? pz : fallback.z,
          bonusXp,
          { mobType: type, isKillBonus: true }
        );
      }
    }
    if (type === 'ender_dragon') {
      showStatusByKey('status.ender_dragon_defeated', {}, 'level', 3.8);
    }
  },
  onMobDamaged: ({ type, cause, damage, maxHp, position }) => {
    if (cause !== 'player' || !position) return;
    const hitXp = experienceSystem?.getMobHitExperience?.(type, damage, maxHp) || 0;
    if (hitXp <= 0) return;
    spawnExperienceDrops(position.x, position.y + 0.2, position.z, hitXp, { mobType: type, isKillBonus: false });
  },
  onMobDrop: ({ itemId, count, position }) => {
    spawnItemDrop(position.x, position.y + 0.15, position.z, itemId, count);
  }
});
mobSystem.setCreativeMode?.(isCreativeMode());

function setPauseMenuVisible(visible) {
  pauseMenuVisible = !!visible;
  if (instructionsEl) instructionsEl.style.display = pauseMenuVisible ? 'flex' : 'none';

  if (!pauseMenuVisible) {
    setPhilosophyPanelOpen(false);
    return;
  }

  stopMining();
  cancelBowCharge();
  stopShieldBlocking();
  player.resetInput();
  blockInteraction.clearPreview();
  heldItem.clear();
}

function consumeUnlockBehavior() {
  const behavior = nextUnlockBehavior;
  nextUnlockBehavior = 'auto';
  return behavior;
}

function syncRuleToggleUI() {
  if (keepInventoryToggleEl) keepInventoryToggleEl.checked = shouldKeepInventoryOnDeath();
  if (creativeModeToggleEl) creativeModeToggleEl.checked = isCreativeMode();
  if (coordinatesToggleEl) coordinatesToggleEl.checked = isCoordinatesShown();
  if (bgmDebugToggleEl) bgmDebugToggleEl.checked = bgmDebugVisible;
}

function setKeepInventoryRule(enabled, { silent = false } = {}) {
  const next = !!enabled;
  if (!gameRules.setKeepInventoryOnDeath(next)) {
    syncRuleToggleUI();
    return false;
  }

  syncRuleToggleUI();

  if (!silent) {
    showStatusByKey(next ? 'status.rule_keep_inventory_on' : 'status.rule_keep_inventory_off', {}, 'level', 2.1);
  }
  return true;
}

function setCreativeModeRule(enabled, { silent = false } = {}) {
  const next = !!enabled;
  if (!gameRules.setCreativeMode(next)) {
    syncRuleToggleUI();
    return false;
  }

  survival?.setCreativeMode(next);
  player?.setCreativeMode(next);
  ui?.setCreativeMode(next);
  mobSystem?.setCreativeMode?.(next);
  syncRuleToggleUI();
  player.resetInput();
  stopMining();
  cancelBowCharge();
  stopShieldBlocking();
  mobUpdateAccumulator = 0;
  fluidUpdateAccumulator = 0;
  dropUpdateAccumulator = 0;
  interactionRefreshAccumulator = 0;

  if (!silent) {
    showStatusByKey(next ? 'status.rule_creative_on' : 'status.rule_creative_off', {}, 'level', 2.25);
  }
  return true;
}

function setCoordinatesRule(enabled, { silent = false } = {}) {
  const next = !!enabled;
  if (!gameRules.setShowCoordinates(next)) {
    syncRuleToggleUI();
    return false;
  }

  syncRuleToggleUI();
  updateCoordinatesHud(0);

  if (!silent) {
    showStatusByKey(next ? 'status.rule_coordinates_on' : 'status.rule_coordinates_off', {}, 'level', 1.8);
  }
  return true;
}

cmdSystem = createCmdSystem({
  controls,
  ui,
  dayNight,
  setCreativeModeRule,
  setKeepInventoryRule,
  showStatusToast,
  ITEM_DEFS,
  world,
});
cmdSystem.mount(
  document.getElementById('cmd-bar'),
  document.getElementById('cmd-input')
);

function formatSaveTimestamp(ms) {
  const ts = Number(ms);
  if (!Number.isFinite(ts) || ts <= 0) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';

  try {
    return new Intl.DateTimeFormat(getLanguage(), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

function refreshSaveMetaUI() {
  const meta = saveGameStore.getMeta();
  const hasSave = !!meta;
  if (saveMetaEl) {
    if (!hasSave) {
      saveMetaEl.innerText = t('menu.save.meta.none');
    } else {
      const timeText = formatSaveTimestamp(meta.savedAt);
      saveMetaEl.innerText = t('menu.save.meta.time', {
        time: timeText || '-'
      });
    }
  }
  if (loadGameBtnEl) loadGameBtnEl.disabled = !hasSave;
  if (deleteSaveBtnEl) deleteSaveBtnEl.disabled = !hasSave;
}

function syncWorldSeedInputUI() {
  if (!worldSeedInputEl) return;
  worldSeedInputEl.value = String(activeWorldSeed);
}

function applyWorldSeedAndReload(nextSeed) {
  const normalized = normalizeWorldSeed(nextSeed, activeWorldSeed);
  if (normalized === activeWorldSeed) {
    showStatusByKey('status.world_seed_unchanged', {}, '', 1.8);
    syncWorldSeedInputUI();
    return false;
  }
  if (!persistPreferredWorldSeed(normalized)) {
    showStatusByKey('status.world_seed_save_failed', {}, '', 2.2);
    syncWorldSeedInputUI();
    return false;
  }
  if (worldSeedInputEl) worldSeedInputEl.value = String(normalized);
  showStatusByKey('status.world_seed_saved', { seed: normalized }, 'level', 1.2);
  window.setTimeout(() => {
    window.location.reload();
  }, 160);
  return true;
}

function applyWorldSeedFromInput() {
  const parsed = parseWorldSeedInput(worldSeedInputEl?.value);
  if (parsed === null) {
    showStatusByKey('status.world_seed_invalid', {}, '', 2.0);
    syncWorldSeedInputUI();
    return false;
  }
  return applyWorldSeedAndReload(parsed);
}

function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderFormattedInlineText(value = '') {
  return String(value)
    .split(/(~~[\s\S]+?~~)/g)
    .map((part) => {
      if (part.startsWith('~~') && part.endsWith('~~')) {
        return `<del>${escapeHtml(part.slice(2, -2))}</del>`;
      }
      return escapeHtml(part);
    })
    .join('');
}

function renderPhilosophyParagraph(text = '') {
  const lines = String(text).split('\n');
  return `<p>${lines.map(renderFormattedInlineText).join('<br>')}</p>`;
}

function renderPhilosophyPanelContent() {
  if (!philosophyContentEl) return;
  const lang = getLanguage();
  const content = GAME_PHILOSOPHY_CONTENT[lang] || GAME_PHILOSOPHY_CONTENT.en;
  const sections = Array.isArray(content?.sections) ? content.sections : [];
  const features = Array.isArray(content?.features) ? content.features : [];
  const sectionsHtml = sections.map(renderPhilosophyParagraph).join('');
  const featureTitleHtml = content?.featuresTitle
    ? `<p class="philosophy-feature-label">${renderFormattedInlineText(content.featuresTitle)}</p>`
    : '';
  const featureListHtml = features.length > 0
    ? `<ol>${features.map((item) => `<li>${String(item).split('\n').map(renderFormattedInlineText).join('<br>')}</li>`).join('')}</ol>`
    : '';
  const footerHtml = content?.footer ? renderPhilosophyParagraph(content.footer) : '';
  philosophyContentEl.innerHTML = `${sectionsHtml}${featureTitleHtml}${featureListHtml}${footerHtml}`;
}

function setPhilosophyPanelOpen(open) {
  const next = !!open;
  philosophyPanelOpen = next;
  if (philosophyScrimEl) philosophyScrimEl.classList.toggle('open', next);
  if (philosophyPanelEl) {
    philosophyPanelEl.classList.toggle('open', next);
    philosophyPanelEl.setAttribute('aria-hidden', next ? 'false' : 'true');
  }
}

function collectGameSnapshot() {
  const pos = controls.getObject().position;
  const respawnAnchor = respawnSystem?.getRespawnAnchor?.() || null;
  return {
    player: {
      x: pos.x,
      y: pos.y,
      z: pos.z
    },
    bedRespawnAnchor: respawnAnchor,
    gameRules: gameRules.getRules(),
    world: {
      manual: world.exportManualState(),
      boats: boatSystem?.exportState?.() || null
    },
    inventory: ui.exportState?.() || null,
    objectives: objectiveSystem?.exportState?.() || null,
    dayNight: dayNight.exportState?.() || null,
    experience: experienceSystem?.exportState?.() || null,
    survival: survival?.exportState?.() || null,
    furnace: furnaceSystem?.exportState?.() || null,
    chest: chestSystem?.exportState?.() || null,
    farming: farmingSystem?.exportState?.() || null,
    villagerTrades: villagerTradeSystem?.exportState?.() || null
  };
}

function applyGameSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return false;

  const savedPos = snapshot.player || {};
  const fallbackPos = getWorldSpawnPosition();
  const px = Number(savedPos.x);
  const py = Number(savedPos.y);
  const pz = Number(savedPos.z);
  const targetX = Number.isFinite(px) ? px : fallbackPos.x;
  const targetY = Number.isFinite(py) ? py : fallbackPos.y;
  const targetZ = Number.isFinite(pz) ? pz : fallbackPos.z;

  const savedRules = snapshot.gameRules;
  if (savedRules && typeof savedRules === 'object') {
    if (Object.prototype.hasOwnProperty.call(savedRules, 'keepInventoryOnDeath')) {
      setKeepInventoryRule(!!savedRules.keepInventoryOnDeath, { silent: true });
    }
    if (Object.prototype.hasOwnProperty.call(savedRules, 'creativeMode')) {
      setCreativeModeRule(!!savedRules.creativeMode, { silent: true });
    }
    if (Object.prototype.hasOwnProperty.call(savedRules, 'showCoordinates')) {
      setCoordinatesRule(!!savedRules.showCoordinates, { silent: true });
    }
  }
  syncRuleToggleUI();

  stopMining();
  cancelBowCharge();
  stopShieldBlocking();
  player.resetInput();
  ui.setCraftOpen(false);
  if (furnaceSystem?.isOpen()) furnaceSystem.close();
  if (chestSystem?.isOpen()) chestSystem.close();
  if (villagerTradeSystem?.isOpen()) villagerTradeSystem.close();
  blockInteraction.clearPreview();
  mobSystem.clearAll();
  deathRecovery.clearDeathRecoveryMarker();
  boatSystem?.clearAll?.();

  world.importManualState(snapshot.world?.manual || null);
  world.reloadTerrain({ x: targetX, z: targetZ });
  boatSystem?.importState?.(snapshot.world?.boats || null);

  furnaceSystem?.importState?.(snapshot.furnace);
  chestSystem?.importState?.(snapshot.chest);
  farmingSystem?.importState?.(snapshot.farming);
  villagerTradeSystem?.importState?.(snapshot.villagerTrades);

  ui.importState?.(snapshot.inventory);
  dayNight.importState?.(snapshot.dayNight);
  objectiveSystem?.importState?.(snapshot.objectives);
  experienceSystem?.importState?.(snapshot.experience);
  survival?.importState?.(snapshot.survival);

  controls.getObject().position.set(targetX, targetY, targetZ);
  respawnSystem?.setRespawnAnchor?.(snapshot.bedRespawnAnchor);

  portalChargeTime = 0;
  portalCooldownTime = 0;
  tableAccessRefreshTimer = 0.22;

  world.updateStreaming(player.getPosition(), 0, 64, {
    viewDistance: getAdaptiveStreamingViewDistance()
  });
  ui.setTableAccess(hasTableAccess());
  refreshBgmContext(true);
  return true;
}

function saveGameNow({ silent = false } = {}) {
  const snapshot = collectGameSnapshot();
  const payload = saveGameStore.write(snapshot);
  refreshSaveMetaUI();

  if (!silent) {
    showStatusByKey(payload ? 'status.save_success' : 'status.save_failed', {}, payload ? 'level' : '', payload ? 1.8 : 2.4);
  }
  return !!payload;
}

function loadGameNow({ silent = false } = {}) {
  const payload = saveGameStore.read();
  if (!payload?.data) {
    refreshSaveMetaUI();
    if (!silent) showStatusByKey('status.load_missing', {}, '', 2.2);
    return false;
  }

  const ok = applyGameSnapshot(payload.data);
  if (!ok) {
    if (!silent) showStatusByKey('status.load_failed', {}, '', 2.2);
    return false;
  }

  refreshSaveMetaUI();
  if (!silent) showStatusByKey('status.load_success', {}, 'level', 2.0);
  return true;
}

function deleteSavedGame({ silent = false } = {}) {
  const removed = saveGameStore.clear();
  refreshSaveMetaUI();
  if (!silent && removed) showStatusByKey('status.save_deleted', {}, '', 1.8);
  return removed;
}

function setupLanguageUI() {
  if (!languageSelectEl) return;

  const options = getLanguageOptions();
  languageSelectEl.innerHTML = '';
  for (const opt of options) {
    const el = document.createElement('option');
    el.value = opt.id;
    el.textContent = opt.label;
    languageSelectEl.appendChild(el);
  }

  languageSelectEl.value = getLanguage();
  languageSelectEl.addEventListener('change', () => {
    setLanguage(languageSelectEl.value);
  });
}

function applyLocalizedText() {
  const lang = getLanguage();
  document.documentElement.lang = lang;
  document.title = `${GAME_BRAND_NAME} - Sandbox Survival`;
  if (languageSelectEl && languageSelectEl.value !== getLanguage()) {
    languageSelectEl.value = getLanguage();
  }

  if (langLabelEl) langLabelEl.innerText = t('hud.language');
  if (instructionsTitleEl) instructionsTitleEl.innerText = GAME_BRAND_NAME;
  if (instructionsSubtitleEl) instructionsSubtitleEl.innerText = GAME_BRAND_SUBTITLE[lang] || GAME_BRAND_SUBTITLE.en;
  if (instructionLine1El) instructionLine1El.innerText = t('main.control.move');
  if (instructionLine2El) instructionLine2El.innerText = t('main.control.swim');
  if (instructionLine3El) instructionLine3El.innerText = t('main.control.action');
  if (instructionLine4El) instructionLine4El.innerText = t('main.control.inventory');
  if (startBtnEl) startBtnEl.innerText = t('main.start');
  if (philosophyBtnEl) philosophyBtnEl.innerText = t('menu.philosophy.open');
  if (philosophyTitleEl) philosophyTitleEl.innerText = t('menu.philosophy.title');
  if (philosophyCloseBtnEl) philosophyCloseBtnEl.innerText = t('menu.philosophy.close');
  renderPhilosophyPanelContent();
  deathFeedbackSystem?.refreshLocale?.();
  if (menuRulesTitleEl) menuRulesTitleEl.innerText = t('menu.rules_title');
  if (keepInventoryLabelEl) keepInventoryLabelEl.innerText = t('menu.rule.keep_inventory');
  if (creativeModeLabelEl) creativeModeLabelEl.innerText = t('menu.rule.creative_mode');
  if (coordinatesLabelEl) coordinatesLabelEl.innerText = t('menu.rule.show_coordinates');
  if (bgmDebugLabelEl) bgmDebugLabelEl.innerText = t('menu.rule.show_bgm_panel');
  if (menuAudioTitleEl) menuAudioTitleEl.innerText = t('menu.audio.title');
  if (viewDistanceLabelEl) viewDistanceLabelEl.innerText = t('menu.video.view_distance');
  if (textureStyleLabelEl) textureStyleLabelEl.innerText = t('menu.video.texture_style');
  if (musicVolumeLabelEl) musicVolumeLabelEl.innerText = t('menu.audio.music');
  if (sfxVolumeLabelEl) sfxVolumeLabelEl.innerText = t('menu.audio.sfx');
  if (menuSeedTitleEl) menuSeedTitleEl.innerText = t('menu.seed.title');
  if (worldSeedLabelEl) worldSeedLabelEl.innerText = t('menu.seed.label');
  if (worldSeedApplyBtnEl) worldSeedApplyBtnEl.innerText = t('menu.seed.action.apply');
  if (worldSeedRandomBtnEl) worldSeedRandomBtnEl.innerText = t('menu.seed.action.random');
  if (worldSeedHintEl) worldSeedHintEl.innerText = t('menu.seed.hint');
  if (menuSaveTitleEl) menuSaveTitleEl.innerText = t('menu.save.title');
  if (saveGameBtnEl) saveGameBtnEl.innerText = t('menu.save.action.save');
  if (loadGameBtnEl) loadGameBtnEl.innerText = t('menu.save.action.load');
  if (deleteSaveBtnEl) deleteSaveBtnEl.innerText = t('menu.save.action.delete');
  syncWorldSeedInputUI();
  syncRuleToggleUI();
  syncAudioSettingsUI();
  syncViewDistanceUI();
  syncTextureStyleUI();
  refreshSaveMetaUI();

  ui.refreshLocale();
  survival.refreshLocale();
  dayNight.refreshLocale();
  mobSystem?.refreshLocale?.();
  objectiveSystem?.refreshLocale();
  furnaceSystem?.refreshLocale();
  chestSystem?.refreshLocale();
  experienceSystem?.refreshLocale();
  villagerTradeSystem?.refreshLocale();
  updateCoordinatesHud(0);
  deathRecovery.updateDeathRecoveryHud(0);
}

function isCraftTableNearby() {
  const p = player.getPosition();
  const px = Math.round(p.x);
  const py = Math.round(player.getFeetY() + 0.5);
  const pz = Math.round(p.z);

  for (let x = px - 3; x <= px + 3; x++) {
    for (let y = py - 2; y <= py + 2; y++) {
      for (let z = pz - 3; z <= pz + 3; z++) {
        if (world.getBlockIdAt(x, y, z) === 11) return true;
      }
    }
  }

  return false;
}

function hasTableAccess() {
  return isCraftTableNearby();
}

function forcePointerUnlock(behavior = 'hide') {
  nextUnlockBehavior = behavior;
  if (controls.isLocked) controls.unlock();
  if (document.pointerLockElement) document.exitPointerLock();
}

function openInventory(mode = 'hand') {
  if (furnaceSystem?.isOpen()) furnaceSystem.close();
  if (chestSystem?.isOpen()) chestSystem.close();
  if (villagerTradeSystem?.isOpen()) villagerTradeSystem.close();
  ui.setCraftOpen(true, {
    tableAccess: hasTableAccess(),
    mode
  });
  ui.setTableAccess(hasTableAccess());

  player.resetInput();
  cancelBowCharge();
  stopShieldBlocking();
  setPauseMenuVisible(false);

  forcePointerUnlock('hide');
  setTimeout(() => forcePointerUnlock('hide'), 0);
  setTimeout(() => forcePointerUnlock('hide'), 48);

  blockInteraction.clearPreview();
  stopMining();
}

function openFurnace(block) {
  if (chestSystem?.isOpen()) chestSystem.close();
  if (villagerTradeSystem?.isOpen()) villagerTradeSystem.close();
  const table = hasTableAccess();
  ui.setCraftOpen(true, {
    tableAccess: table,
    mode: table ? 'table' : 'hand'
  });
  ui.setTableAccess(table);
  if (!furnaceSystem?.openAtBlock(block)) {
    ui.setCraftOpen(false);
    return false;
  }

  player.resetInput();
  cancelBowCharge();
  stopShieldBlocking();
  setPauseMenuVisible(false);

  forcePointerUnlock('hide');
  setTimeout(() => forcePointerUnlock('hide'), 0);
  setTimeout(() => forcePointerUnlock('hide'), 48);

  blockInteraction.clearPreview();
  stopMining();
  return true;
}

function openChest(block) {
  if (furnaceSystem?.isOpen()) furnaceSystem.close();
  if (villagerTradeSystem?.isOpen()) villagerTradeSystem.close();
  const table = hasTableAccess();
  ui.setCraftOpen(true, {
    tableAccess: table,
    mode: table ? 'table' : 'hand'
  });
  ui.setTableAccess(table);
  if (!chestSystem?.openAtBlock(block)) {
    ui.setCraftOpen(false);
    return false;
  }

  player.resetInput();
  cancelBowCharge();
  stopShieldBlocking();
  setPauseMenuVisible(false);

  forcePointerUnlock('hide');
  setTimeout(() => forcePointerUnlock('hide'), 0);
  setTimeout(() => forcePointerUnlock('hide'), 48);

  blockInteraction.clearPreview();
  stopMining();
  return true;
}

function openVillagerTrade(villager) {
  if (ui.isCraftOpen()) ui.setCraftOpen(false);
  if (furnaceSystem?.isOpen()) furnaceSystem.close();
  if (chestSystem?.isOpen()) chestSystem.close();
  if (!villagerTradeSystem?.openForVillager(villager)) return false;

  player.resetInput();
  cancelBowCharge();
  stopShieldBlocking();
  setPauseMenuVisible(false);

  forcePointerUnlock('hide');
  setTimeout(() => forcePointerUnlock('hide'), 0);
  setTimeout(() => forcePointerUnlock('hide'), 48);

  blockInteraction.clearPreview();
  stopMining();
  return true;
}

function toggleInteractiveBlock(block) {
  const id = block?.userData?.id;
  let targetId = null;
  if (id === 16) targetId = 17;
  else if (id === 17) targetId = 16;
  else if (id === 18) targetId = 19;
  else if (id === 19) targetId = 18;
  if (targetId === null) return false;

  const x = Math.round(block.position.x);
  const y = Math.round(block.position.y);
  const z = Math.round(block.position.z);

  world.removeBlock(block, { trackModification: true });
  const toggled = world.createBlock(x, y, z, targetId);
  if (!toggled) {
    world.createBlock(x, y, z, id);
    return false;
  }

  sounds.placeByBlockId(targetId);
  return true;
}

function findSafeRespawnNear(x, y, z) {
  const offsets = [
    [0, 0],
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
    [2, 0],
    [-2, 0],
    [0, 2],
    [0, -2]
  ];

  for (const [dx, dz] of offsets) {
    const sx = x + dx;
    const sz = z + dz;
    for (let floorY = y + 1; floorY >= y - 2; floorY--) {
      if (!world.isSolidAt(sx, floorY, sz)) continue;
      if (world.isSolidAt(sx, floorY + 1, sz)) continue;
      if (world.isSolidAt(sx, floorY + 2, sz)) continue;
      const standHeight = Math.max(1.8, player.getHeight());
      return new THREE.Vector3(sx, floorY + standHeight + 0.52, sz);
    }
  }

  return null;
}

function closeAllWindowsAndResume() {
  let closedAnyWindow = false;

  if (ui.isCraftOpen()) {
    ui.setCraftOpen(false);
    closedAnyWindow = true;
  }

  if (furnaceSystem?.isOpen()) {
    furnaceSystem.close();
    closedAnyWindow = true;
  }

  if (chestSystem?.isOpen()) {
    chestSystem.close();
    closedAnyWindow = true;
  }

  if (villagerTradeSystem?.isOpen()) {
    villagerTradeSystem.close();
    closedAnyWindow = true;
  }

  if (cmdSystem?.isOpen()) {
    cmdSystem.close();
    closedAnyWindow = true;
  }

  if (pauseMenuVisible) {
    closedAnyWindow = true;
  }

  if (!closedAnyWindow) return false;

  player.resetInput();
  stopMining();
  cancelBowCharge();
  stopShieldBlocking();
  blockInteraction.clearPreview();

  nextUnlockBehavior = 'auto';
  if (!controls.isLocked) controls.lock();
  if (pauseMenuVisible && controls.isLocked) setPauseMenuVisible(false);
  sounds.resume();
  bgmController.unlock();

  return true;
}

portalSystem = createPortalSystem({
  world,
  ui,
  player,
  controls,
  sounds,
  isCreativeMode,
  showStatusByKey,
  refreshBgmContext,
  stopMining,
  cancelBowCharge,
  stopShieldBlocking,
  findSafeRespawnNear,
  t
});

const playerCombatFluidSystem = createPlayerCombatFluidSystem({
  camera,
  world,
  player,
  ui,
  itemDefs: ITEM_DEFS,
  mobSystem,
  heldItem,
  sounds,
  particles,
  blockInteraction,
  isCreativeMode,
  getRangedCooldown: () => rangedCooldown,
  setRangedCooldown: (value) => {
    rangedCooldown = Math.max(0, Number(value) || 0);
  },
  getBowChargeState: () => ({
    charging: bowCharging,
    startTime: bowChargeStartTime
  })
});

function tryAttackMob(blockHit = null) {
  return playerCombatFluidSystem.tryAttackMob(blockHit);
}

const worldInteractSystem = createWorldInteractSystem({
  camera,
  ui,
  mobSystem,
  heldItem,
  sounds,
  isCreativeMode,
  openVillagerTrade,
  openInventory,
  openFurnace,
  openChest,
  toggleInteractiveBlock,
  tryUseBed: (block) => respawnSystem?.tryUseBed?.(block),
  tryIgnitePortalFromHitFace,
  tryIgnitePortalFromObsidianBlock,
  tryIgniteFireFromHitFace,
  tryActivateEndPortalFromQuartzBlock,
  tryToggleLever: (block) => redstoneSystem?.tryToggleLever(block),
  tryUseFarmingOnBlock: (block) => farmingSystem?.tryUseOnBlock(block),
  obsidianBlockId: OBSIDIAN_BLOCK_ID,
  quartzBlockId: QUARTZ_BLOCK_ID
});

function tryShootBow() {
  return playerCombatFluidSystem.tryShootBow();
}

function tryScoopFluidWithBucket(hit) {
  return playerCombatFluidSystem.tryScoopFluidWithBucket(hit);
}

function tryPourFluidFromBucket(hit, activeItemId) {
  return playerCombatFluidSystem.tryPourFluidFromBucket(hit, activeItemId);
}

function isMeleeAttackReady() {
  return meleeCooldown <= 0;
}

function onMeleeAttackSuccess() {
  heldItem.triggerSwing(1.1);
  meleeCooldown = 0.46;
}

function tryStartMiningFromHit(hit, opts = {}) {
  if (boatSystem?.tryBreakBoatByHit?.(hit)) return false;
  if (!hit || hit.object.userData.mineable === false) return false;
  const fromHold = !!opts.fromHold;
  if (fromHold && isCreativeMode() && creativeHoldMineCooldown > 0) return false;

  isMining = true;
  miningTarget = hit.object;
  miningProgress = 0;
  miningAimLostTimer = 0;
  miningRequiredTime = 0.12;
  miningPulse = 0.55;
  lastHitSoundTime = 0;
  heldItem.triggerSwing(0.92);
  return true;
}

function tryStartShieldBlockingByActiveItem() {
  if (ui.getActiveItemId() !== 'shield' || ui.getActiveItemCount() <= 0) return false;
  isBlockingWithShield = true;
  return true;
}

function tryStartBowChargeByActiveItem() {
  if (ui.getActiveItemId() !== 'bow' || ui.getActiveItemCount() <= 0) return false;
  if ((isCreativeMode() || ui.hasItem('arrow', 1)) && rangedCooldown <= 0) {
    bowCharging = true;
    bowChargeStartTime = performance.now();
    heldItem.triggerSwing(0.22);
  }
  return true;
}

function tryEquipArmorByActiveItem() {
  if (!ui.tryEquipActiveArmor()) return false;
  if (sounds.equip) sounds.equip();
  return true;
}

function tryConsumeFoodByActiveItem() {
  return survival.tryConsumeActiveFood();
}

function worldCoordToBlock(v) {
  return Math.floor(v + 0.5);
}

function getTouchingCactusSourcePosition(playerPos = null, feetY = null, height = null) {
  const pos = playerPos || player.getPosition();
  const sampleFeetY = Number.isFinite(feetY) ? feetY : player.getFeetY();
  const sampleHeight = Math.max(1.5, Number.isFinite(height) ? height : player.getHeight());
  const sampleRadius = CACTUS_TOUCH_SAMPLE_RADIUS;
  const sampleOffsets = [
    [0, 0],
    [sampleRadius, 0],
    [-sampleRadius, 0],
    [0, sampleRadius],
    [0, -sampleRadius],
    [sampleRadius * 0.72, sampleRadius * 0.72],
    [sampleRadius * 0.72, -sampleRadius * 0.72],
    [-sampleRadius * 0.72, sampleRadius * 0.72],
    [-sampleRadius * 0.72, -sampleRadius * 0.72]
  ];
  const sampleY = [
    sampleFeetY - 0.04,
    sampleFeetY + 0.46,
    sampleFeetY + Math.max(0.9, sampleHeight * 0.58),
    pos.y - 0.14
  ];

  let nearest = null;
  let nearestDistSq = Infinity;
  for (const y of sampleY) {
    for (const [dx, dz] of sampleOffsets) {
      const sx = pos.x + dx;
      const sz = pos.z + dz;
      if (world.getBlockIdAt(sx, y, sz) !== CACTUS_BLOCK_ID) continue;
      const bx = worldCoordToBlock(sx);
      const by = worldCoordToBlock(y);
      const bz = worldCoordToBlock(sz);
      const distSq = ((pos.x - bx) * (pos.x - bx)) + ((y - by) * (y - by)) + ((pos.z - bz) * (pos.z - bz));
      if (distSq >= nearestDistSq) continue;
      nearestDistSq = distSq;
      nearest = { x: bx, y: by, z: bz };
    }
  }
  return nearest;
}

function tryUseBoatByHit(hit) {
  return !!boatSystem?.tryUseBoatByHit?.(hit);
}

function tryScoopFluidByHit(hit) {
  if (ui.getActiveItemId() !== 'bucket') return false;
  return tryScoopFluidWithBucket(hit);
}

function tryPourFluidByHit(hit) {
  const activeItem = ui.getActiveItemId();
  if (activeItem !== 'water_bucket' && activeItem !== 'lava_bucket') return false;
  return tryPourFluidFromBucket(hit, activeItem);
}

function tryPlaceActiveBlockFromHotbar() {
  const activeItem = ui.getActiveItemId();
  const placeBlockId = blockInteraction.getActivePlaceBlockId();
  if (!activeItem || placeBlockId === null) return false;

  const candidate = blockInteraction.refreshPlacementPreview(true);
  if (!candidate) return false;

  if ((placeBlockId === 16 || placeBlockId === 18 || placeBlockId === 20 || placeBlockId === 58 || placeBlockId === 60)
    && !world.isSolidAt(candidate.x, candidate.y - 1, candidate.z)) {
    return false;
  }

  const placed = world.createBlock(candidate.x, candidate.y, candidate.z, placeBlockId);
  if (!placed) return false;

  if (!isCreativeMode()) {
    if (!ui.consumeActiveItem(1)) {
      world.removeBlock(placed, { trackModification: true });
      return false;
    }
  }

  sounds.placeByBlockId(placeBlockId);
  heldItem.triggerSwing(0.34);
  objectiveSystem?.onPlaceBlock(placeBlockId, 1);
  farmingSystem?.onBlockPlaced(candidate.x, candidate.y, candidate.z, placeBlockId);
  redstoneSystem?.onBlockPlaced(candidate.x, candidate.y, candidate.z, placeBlockId);
  world.settleGravityAround(candidate.x, candidate.y, candidate.z, 0);
  ui.setTableAccess(hasTableAccess());
  blockInteraction.refreshPlacementPreview(true);
  return true;
}

function tryShootBowOnMouseRelease() {
  if (controls.isLocked && !isUiWindowOpen()) {
    tryShootBow();
  }
}

function dropFurnaceContentsAt(x, y, z) {
  furnaceSystem?.ejectContentsAt(x, y, z, ({ itemId, count, position }) => {
    const def = ITEM_DEFS[itemId];
    world.spawnDrop(position.x, position.y + 0.16, position.z, itemId, {
      kind: 'item',
      color: def?.color,
      count
    });
  });
}

function dropChestContentsAt(x, y, z) {
  chestSystem?.ejectContentsAt(x, y, z, ({ itemId, count, position }) => {
    const def = ITEM_DEFS[itemId];
    world.spawnDrop(position.x, position.y + 0.16, position.z, itemId, {
      kind: 'item',
      color: def?.color,
      count
    });
  });
}

function cleanupUnsupportedFixturesAround(x, y, z) {
  const pending = [];
  for (let bx = x - 1; bx <= x + 1; bx++) {
    for (let by = y - 1; by <= y + 2; by++) {
      for (let bz = z - 1; bz <= z + 1; bz++) {
        const block = world.getBlockAt(bx, by, bz);
        if (!block) continue;
        const id = block.userData?.id;
        const needsSupport = (
          id === 16 || id === 17 || id === 18 || id === 19 || id === 20
          || id === 49
          || id === 65
          || id === 58 || id === 59 || id === 60 || id === 61
        );
        if (needsSupport && !world.isSolidAt(bx, by - 1, bz)) {
          pending.push(block);
          continue;
        }

        if (farmingSystem?.isCropBlockId(id) && world.getBlockIdAt(bx, by - 1, bz) !== 49) {
          pending.push(block);
        }
      }
    }
  }

  for (const block of pending) {
    const removedId = block.userData.id;
    const handledCropDrop = !!(farmingSystem?.isCropBlockId(removedId) && farmingSystem.handleCropBreakByMining(block));
    if (!handledCropDrop) {
      const dropItemId = getDropItemId(removedId);
      if (dropItemId) {
        spawnItemDrop(block.position.x, block.position.y, block.position.z, dropItemId, 1);
      }
    }
    world.removeBlock(block, { trackModification: true });
    farmingSystem?.onBlockRemoved(
      Math.round(block.position.x),
      Math.round(block.position.y),
      Math.round(block.position.z),
      removedId
    );
  }

  redstoneSystem?.recomputeNetworkAround(x, y, z);

  cleanupBrokenPortalBlocksNear(x, y, z);
}

function onCreeperExplode(pos) {
  sounds.explosion();
  const radius = 2.2;

  for (let x = Math.floor(pos.x - radius); x <= Math.ceil(pos.x + radius); x++) {
    for (let y = Math.floor(pos.y - radius); y <= Math.ceil(pos.y + radius); y++) {
      for (let z = Math.floor(pos.z - radius); z <= Math.ceil(pos.z + radius); z++) {
        const d = Math.hypot(x - pos.x, y - pos.y, z - pos.z);
        if (d > radius) continue;

        const block = world.getBlockAt(x, y, z);
        if (!block) continue;
        if (block.userData.id === 7) continue;
        if (Math.random() > 0.55) continue;

        if (block.userData.id === 13) {
          dropFurnaceContentsAt(block.position.x, block.position.y, block.position.z);
        }
        if (block.userData.id === 14) {
          dropChestContentsAt(block.position.x, block.position.y, block.position.z);
        }
        if (farmingSystem?.isCropBlockId(block.userData.id)) {
          farmingSystem.handleCropBreakByMining(block);
        }

        const removedId = block.userData.id;
        world.removeBlock(block, { trackModification: true });
        farmingSystem?.onBlockRemoved(x, y, z, removedId);
        cleanupUnsupportedFixturesAround(x, y, z);
      }
    }
  }

  world.settleGravityAround(pos.x, pos.y, pos.z, 3);
}

function onBedExplode(pos) {
  sounds.explosion();
  const radius = 2.75;
  const blastResistant = new Set([31, 43, 46]);

  for (let x = Math.floor(pos.x - radius); x <= Math.ceil(pos.x + radius); x++) {
    for (let y = Math.floor(pos.y - radius); y <= Math.ceil(pos.y + radius); y++) {
      for (let z = Math.floor(pos.z - radius); z <= Math.ceil(pos.z + radius); z++) {
        const d = Math.hypot(x - pos.x, y - pos.y, z - pos.z);
        if (d > radius) continue;

        const block = world.getBlockAt(x, y, z);
        if (!block) continue;
        if (block.userData.id === 7 || blastResistant.has(block.userData.id)) continue;

        const breakChance = THREE.MathUtils.clamp(0.88 - (d / radius) * 0.58, 0.12, 0.9);
        if (Math.random() > breakChance) continue;

        if (block.userData.id === 13) {
          dropFurnaceContentsAt(block.position.x, block.position.y, block.position.z);
        }
        if (block.userData.id === 14) {
          dropChestContentsAt(block.position.x, block.position.y, block.position.z);
        }
        if (farmingSystem?.isCropBlockId(block.userData.id)) {
          farmingSystem.handleCropBreakByMining(block);
        }

        const removedId = block.userData.id;
        world.removeBlock(block, { trackModification: true });
        farmingSystem?.onBlockRemoved(x, y, z, removedId);
        cleanupUnsupportedFixturesAround(x, y, z);
      }
    }
  }

  const playerPos = controls.getObject().position;
  const playerDist = playerPos.distanceTo(pos);
  const damageRadius = radius + 1.05;
  if (playerDist < damageRadius) {
    const falloff = Math.max(0, 1 - playerDist / damageRadius);
    const damage = Math.max(4, Math.round(18 * falloff));
    survival.applyDamage(damage, 'bed_explode', { sourcePosition: pos });
  }

  world.settleGravityAround(pos.x, pos.y, pos.z, 3);
}

function respawnPlayer() {
  const deathPos = controls.getObject().position.clone();
  const recoverySource = `death_inventory_${deathRecovery.nextBatchSeq()}`;
  const dropped = dropPlayerInventoryAt(deathPos, { source: recoverySource });
  if (dropped > 0) {
    deathRecovery.activateDeathRecoveryMarker(deathPos, recoverySource, dropped);
  } else {
    deathRecovery.clearDeathRecoveryMarker();
  }
  survival.reset();
  portalChargeTime = 0;
  portalCooldownTime = 0;
  player.resetInput();
  stopMining();
  cancelBowCharge();
  stopShieldBlocking();
  boatSystem?.dismountMountedBoat?.({ skipReposition: true, silent: true });
  ui.setCraftOpen(false);
  if (furnaceSystem?.isOpen()) furnaceSystem.close();
  if (chestSystem?.isOpen()) chestSystem.close();
  if (villagerTradeSystem?.isOpen()) villagerTradeSystem.close();
  deathFeedbackSystem?.resetDeathCameraRoll?.();
  controls.getObject().position.copy(respawnSystem?.resolveRespawnPosition?.() || getWorldSpawnPosition());
  refreshBgmContext(true);
  if (dropped > 0) {
    showStatusByKey('status.death_dropped_items', { count: dropped }, '', 2.8);
  }
}

if (startBtnEl) {
  startBtnEl.addEventListener('click', () => {
    setPhilosophyPanelOpen(false);
    nextUnlockBehavior = 'auto';
    controls.lock();
    sounds.resume();
    bgmController.unlock();
  });
}

if (philosophyBtnEl) {
  philosophyBtnEl.addEventListener('click', () => {
    setPhilosophyPanelOpen(true);
  });
}

if (philosophyCloseBtnEl) {
  philosophyCloseBtnEl.addEventListener('click', () => {
    setPhilosophyPanelOpen(false);
  });
}

if (philosophyScrimEl) {
  philosophyScrimEl.addEventListener('click', () => {
    setPhilosophyPanelOpen(false);
  });
}

if (keepInventoryToggleEl) {
  keepInventoryToggleEl.checked = shouldKeepInventoryOnDeath();
  keepInventoryToggleEl.addEventListener('change', () => {
    setKeepInventoryRule(keepInventoryToggleEl.checked);
  });
}

if (creativeModeToggleEl) {
  creativeModeToggleEl.checked = isCreativeMode();
  creativeModeToggleEl.addEventListener('change', () => {
    setCreativeModeRule(creativeModeToggleEl.checked);
  });
}

if (coordinatesToggleEl) {
  coordinatesToggleEl.checked = isCoordinatesShown();
  coordinatesToggleEl.addEventListener('change', () => {
    setCoordinatesRule(coordinatesToggleEl.checked);
  });
}

if (bgmDebugToggleEl) {
  bgmDebugToggleEl.checked = bgmDebugVisible;
  bgmDebugToggleEl.addEventListener('change', () => {
    setBgmDebugVisible(bgmDebugToggleEl.checked, { persist: true, toast: true });
  });
}

if (viewDistanceSliderEl) {
  viewDistanceSliderEl.value = `${clampPreferredViewDistance(preferredViewDistance)}`;
  viewDistanceSliderEl.addEventListener('input', () => {
    setPreferredViewDistance(viewDistanceSliderEl.value, { persist: false });
  });
  viewDistanceSliderEl.addEventListener('change', () => {
    setPreferredViewDistance(viewDistanceSliderEl.value, { persist: true });
  });
}

if (textureStyleSelectEl) {
  textureStyleSelectEl.value = normalizeTextureStyle(preferredTextureStyle);
  textureStyleSelectEl.addEventListener('change', () => {
    setPreferredTextureStyle(textureStyleSelectEl.value, { persist: true, toast: true });
  });
}

if (musicVolumeSliderEl) {
  musicVolumeSliderEl.value = `${Math.round(clamp01(musicVolume) * 100)}`;
  musicVolumeSliderEl.addEventListener('input', () => {
    setMusicVolume((Number(musicVolumeSliderEl.value) || 0) / 100, { persist: false });
  });
  musicVolumeSliderEl.addEventListener('change', () => {
    setMusicVolume((Number(musicVolumeSliderEl.value) || 0) / 100, { persist: true });
  });
}

if (sfxVolumeSliderEl) {
  sfxVolumeSliderEl.value = `${Math.round(clamp01(sfxVolume) * 100)}`;
  sfxVolumeSliderEl.addEventListener('input', () => {
    setSfxVolume((Number(sfxVolumeSliderEl.value) || 0) / 100, { persist: false });
  });
  sfxVolumeSliderEl.addEventListener('change', () => {
    setSfxVolume((Number(sfxVolumeSliderEl.value) || 0) / 100, { persist: true });
  });
}

if (worldSeedInputEl) {
  worldSeedInputEl.value = String(activeWorldSeed);
  worldSeedInputEl.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    applyWorldSeedFromInput();
  });
}

if (worldSeedApplyBtnEl) {
  worldSeedApplyBtnEl.addEventListener('click', () => {
    applyWorldSeedFromInput();
  });
}

if (worldSeedRandomBtnEl) {
  worldSeedRandomBtnEl.addEventListener('click', () => {
    const nextSeed = createRandomWorldSeed(activeWorldSeed);
    applyWorldSeedAndReload(nextSeed);
  });
}

if (saveGameBtnEl) {
  saveGameBtnEl.addEventListener('click', () => {
    saveGameNow();
  });
}

if (loadGameBtnEl) {
  loadGameBtnEl.addEventListener('click', () => {
    loadGameNow();
  });
}

if (deleteSaveBtnEl) {
  deleteSaveBtnEl.addEventListener('click', () => {
    deleteSavedGame();
  });
}

controls.addEventListener('lock', () => {
  nextUnlockBehavior = 'auto';
  setPauseMenuVisible(false);
  tryEnableKeyboardLock();
  bgmController.unlock();
});

controls.addEventListener('unlock', () => {
  releaseKeyboardLock();

  if (isUiWindowOpen()) {
    nextUnlockBehavior = 'auto';
    setPauseMenuVisible(false);
    return;
  }

  const behavior = consumeUnlockBehavior();
  const shouldPause = behavior !== 'hide';

  setPauseMenuVisible(shouldPause);
  if (!shouldPause) {
    player.resetInput();
    stopMining();
    cancelBowCharge();
    stopShieldBlocking();
    blockInteraction.clearPreview();
  }
});

window.addEventListener('blur', () => {
  nextUnlockBehavior = 'hide';
  releaseKeyboardLock();
  setPauseMenuVisible(false);
  player.resetInput();
  stopMining();
  cancelBowCharge();
  stopShieldBlocking();
  if (furnaceSystem?.isOpen()) furnaceSystem.close();
  if (chestSystem?.isOpen()) chestSystem.close();
  if (villagerTradeSystem?.isOpen()) villagerTradeSystem.close();
  blockInteraction.clearPreview();
});

window.addEventListener('beforeunload', (e) => {
  if (!controls.isLocked || isUiWindowOpen()) return;
  e.preventDefault();
  e.returnValue = '';
});

renderer.domElement.addEventListener('mousedown', () => {
  if (controls.isLocked || isUiWindowOpen() || pauseMenuVisible) return;
  controls.lock();
});

document.addEventListener('contextmenu', (e) => e.preventDefault());

document.addEventListener('compositionstart', () => {
  imeComposing = true;
  if (!controls.isLocked) return;
  player.resetInput();
  stopMining();
  cancelBowCharge();
  stopShieldBlocking();
});

document.addEventListener('compositionend', () => {
  imeComposing = false;
});

document.addEventListener('keydown', (e) => {
  if (isPlayerDeathAnimating()) {
    e.preventDefault();
    e.stopPropagation();
    return;
  }

  if (e.code === 'F8') {
    e.preventDefault();
    e.stopPropagation();
    setPerfHudVisible(!perfHudVisible, { persist: true, toast: true });
    return;
  }

  // Open command line: Enter = command mode (/), T = chat mode (empty)
  if (!cmdSystem?.isOpen() && controls.isLocked && !isUiWindowOpen()) {
    if (e.code === 'Enter') {
      e.preventDefault();
      cmdSystem?.openCmd('command');
      return;
    }
    if (e.code === 'KeyT') {
      e.preventDefault();
      cmdSystem?.openCmd('chat');
      return;
    }
  }

  if (shouldIgnoreGameKeyboardEvent(e)) return;

  if (controls.isLocked && (e.code === 'AltLeft' || e.code === 'AltRight')) {
    e.preventDefault();
    e.stopPropagation();
  }

  if (shouldBlockBrowserShortcut(e)) {
    e.preventDefault();
    e.stopPropagation();
  }

  if (e.code === 'Escape') {
    e.preventDefault();

    if (isUiWindowOpen()) {
      closeAllWindowsAndResume();
      return;
    }

    if (philosophyPanelOpen) {
      setPhilosophyPanelOpen(false);
      return;
    }

    if (pauseMenuVisible) {
      closeAllWindowsAndResume();
      return;
    }

    if (controls.isLocked) {
      forcePointerUnlock('pause');
      return;
    }

    setPauseMenuVisible(true);
    return;
  }

  if (e.code === 'KeyE') {
    if (isUiWindowOpen()) {
      closeAllWindowsAndResume();
    } else if (controls.isLocked) {
      openInventory(hasTableAccess() ? 'table' : 'hand');
    }
    return;
  }

  if (e.code === 'KeyQ') {
    const dropCount = e.ctrlKey ? 999 : 1;
    if (dropActiveItemFromPlayer({ count: dropCount })) {
      e.preventDefault();
      e.stopPropagation();
      stopMining();
      return;
    }
  }

  const hotbarSlot = getHotbarSlotFromKeyEvent(e);
  if (hotbarSlot !== null) {
    ui.setActiveSlot(hotbarSlot);
    blockInteraction.refreshPlacementPreview(true);
  }

  if (controls.isLocked) player.onKeyDown(e);
});

document.addEventListener('keyup', (e) => {
  if (isPlayerDeathAnimating()) return;
  if (shouldIgnoreGameKeyboardEvent(e)) return;

  if (controls.isLocked && (e.code === 'AltLeft' || e.code === 'AltRight')) {
    e.preventDefault();
    e.stopPropagation();
  }
  player.onKeyUp(e);
});

interactionRouterSystem = createInteractionRouterSystem({
  worldInteractSystem,
  tryUseUtility: tryUseBoatByHit,
  tryStartShieldBlocking: tryStartShieldBlockingByActiveItem,
  tryStartBowCharge: tryStartBowChargeByActiveItem,
  tryEquipArmor: tryEquipArmorByActiveItem,
  tryConsumeFood: tryConsumeFoodByActiveItem,
  tryScoopFluid: tryScoopFluidByHit,
  tryPourFluid: tryPourFluidByHit,
  tryPlaceActiveBlock: tryPlaceActiveBlockFromHotbar,
  stopMining
});

const playerInteractionSystem = createPlayerInteractionSystem({
  controls,
  isUiWindowOpen,
  isPlayerDeathAnimating,
  getTargetHit: ({ force = false } = {}) => blockInteraction.getTargetHit(6.0, { force }),
  routeSecondaryInteraction: interactionRouterSystem.routeSecondaryInteraction,
  tryRepeatPlaceAction: interactionRouterSystem.tryRepeatPlace,
  onPrimarySwing: () => heldItem.triggerSwing(0.78),
  isMeleeAttackReady,
  tryAttackMob,
  onMeleeAttackSuccess,
  tryStartMining: tryStartMiningFromHit,
  isMining: () => isMining,
  tryPlaceActiveBlock: tryPlaceActiveBlockFromHotbar,
  stopMining,
  stopShieldBlocking,
  isBowCharging: () => bowCharging,
  tryShootBowOnRelease: tryShootBowOnMouseRelease,
  cancelBowCharge
});
playerInteractionSystem.bind();

setupLanguageUI();
onLanguageChange(() => {
  applyLocalizedText();
});
applyLocalizedText();

let prevTime = performance.now();
const playerPrevPos = new THREE.Vector3();
const playerCurrPos = new THREE.Vector3();
const worldVisibilityDir = new THREE.Vector3();

function updateDynamicResolution(delta) {
  fpsSampleFrames += 1;
  fpsSampleSeconds += delta;
  if (fpsSampleSeconds < 0.75) return;

  const fps = fpsSampleFrames / Math.max(0.001, fpsSampleSeconds);
  fpsSampleFrames = 0;
  fpsSampleSeconds = 0;

  let nextRatio = dynamicPixelRatio;
  if (fps < 50) {
    nextRatio = Math.max(MIN_PIXEL_RATIO, dynamicPixelRatio - 0.08);
  } else if (fps > 58) {
    nextRatio = Math.min(MAX_PIXEL_RATIO, dynamicPixelRatio + 0.04);
  }

  if (Math.abs(nextRatio - dynamicPixelRatio) >= 0.01) {
    dynamicPixelRatio = nextRatio;
    renderer.setPixelRatio(dynamicPixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight, false);
  }
}

function getStreamingBuildBudget(delta) {
  if (delta > 0.03) return 10;
  if (delta > 0.024) return 13;
  if (delta > 0.02) return 16;
  if (delta < 0.012) return 28;
  if (delta < 0.015) return 24;
  return 20;
}

function updateCoordinatesHud(delta = 0.016) {
  if (!coordsHudLabelEl) return;
  const enabled = isCoordinatesShown();
  coordsHudLabelEl.style.display = enabled ? 'block' : 'none';
  if (!enabled) return;

  coordsHudSampleTimer = Math.max(0, coordsHudSampleTimer - Math.max(0, delta));
  if (coordsHudSampleTimer > 0) return;
  coordsHudSampleTimer = 0.08;

  const pos = player.getPosition();
  coordsHudLabelEl.innerText = t('hud.coords_value', {
    x: Math.round(pos.x),
    y: Math.round(pos.y),
    z: Math.round(pos.z)
  });
}

function setBgmDebugVisible(nextVisible, { persist = true, toast = false } = {}) {
  bgmDebugVisible = !!nextVisible;
  if (bgmDebugEl) {
    bgmDebugEl.style.display = bgmDebugVisible ? 'block' : 'none';
  }
  if (bgmDebugToggleEl) bgmDebugToggleEl.checked = bgmDebugVisible;
  if (persist) {
    try {
      localStorage.setItem(BGM_DEBUG_VISIBILITY_KEY, bgmDebugVisible ? '1' : '0');
    } catch {
      // Ignore quota/privacy mode errors.
    }
  }
  if (toast) {
    showStatusByKey(bgmDebugVisible ? 'status.rule_bgm_panel_on' : 'status.rule_bgm_panel_off', {}, 'level', 1.6);
  }
}

function setPerfHudVisible(nextVisible, { persist = true, toast = false } = {}) {
  perfHudVisible = !!nextVisible;
  if (perfHudEl) perfHudEl.style.display = perfHudVisible ? 'block' : 'none';
  if (persist) {
    try {
      localStorage.setItem(PERF_HUD_VISIBILITY_KEY, perfHudVisible ? '1' : '0');
    } catch {
      // Ignore quota/privacy mode errors.
    }
  }
  if (toast) {
    showStatusToast(
      perfHudVisible ? 'Performance HUD: ON' : 'Performance HUD: OFF',
      'level',
      1.6
    );
  }
}

function pushPerfSample(series, value, limit = 120) {
  const safe = Math.max(0, Number(value) || 0);
  series.push(safe);
  if (series.length > limit) series.shift();
}

function meanPerfSample(series) {
  if (!series || series.length <= 0) return 0;
  let sum = 0;
  for (const v of series) sum += v;
  return sum / series.length;
}

function percentilePerfSample(series, p = 0.95) {
  if (!series || series.length <= 0) return 0;
  const sorted = series.slice().sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.floor((sorted.length - 1) * p)));
  return sorted[idx];
}

function getPerformanceIntervals() {
  if (performanceTier <= 0) {
    return {
      mob: 0.034,
      fluid: 0.032,
      drops: 0.028,
      interaction: 0.028
    };
  }
  if (performanceTier >= 2) {
    return {
      mob: 0.016,
      fluid: 0.016,
      drops: 0.016,
      interaction: 0.012
    };
  }
  return {
    mob: 0.022,
    fluid: 0.02,
    drops: 0.02,
    interaction: 0.016
  };
}

function getFluidUpdateOptions(delta = 0.016, playerPosition = null) {
  const safeDelta = Math.max(0, Number(delta) || 0);
  let tier = performanceTier;
  if (safeDelta > 0.03) tier = Math.max(0, tier - 1);
  return {
    performanceTier: tier,
    playerPosition: playerPosition || player.getPosition()
  };
}

function getAdaptiveStreamingBuildBudget(delta) {
  const base = getStreamingBuildBudget(delta);
  if (performanceTier <= 0) return Math.max(6, Math.floor(base * 0.58));
  if (performanceTier >= 2) return Math.min(40, Math.floor(base * 1.2));
  return base;
}

function clampPreferredViewDistance(value) {
  const parsed = Math.floor(Number(value));
  if (!Number.isFinite(parsed)) return 3;
  return THREE.MathUtils.clamp(parsed, 1, 8);
}

function getAdaptiveStreamingViewDistance() {
  const preferred = clampPreferredViewDistance(preferredViewDistance);
  if (performanceTier <= 0) return Math.max(1, preferred - 1);
  if (isCreativeMode() && performanceTier >= 1) return Math.min(8, preferred + 1);
  return preferred;
}

function updatePerformanceGovernor(delta = 0.016) {
  performanceTierSampleTimer = Math.max(0, performanceTierSampleTimer - Math.max(0, delta));
  if (performanceTierSampleTimer > 0) return;
  performanceTierSampleTimer = 0.62;
  if (perfStats.frame.length < 24) return;

  const frameP95 = percentilePerfSample(perfStats.frame, 0.95);
  let targetTier = performanceTier;

  if (frameP95 > 29) {
    targetTier = 0;
  } else if (frameP95 > 22) {
    targetTier = Math.min(targetTier, 1);
  } else if (frameP95 < 13 && dynamicPixelRatio >= (MAX_PIXEL_RATIO - 0.02)) {
    targetTier = 2;
  } else if (frameP95 < 17) {
    targetTier = Math.max(targetTier, 1);
  }

  if (targetTier === performanceTier) {
    performanceTierCandidate = performanceTier;
    performanceTierCandidateStreak = 0;
    return;
  }

  if (targetTier !== performanceTierCandidate) {
    performanceTierCandidate = targetTier;
    performanceTierCandidateStreak = 1;
  } else {
    performanceTierCandidateStreak += 1;
  }

  const isDowngrade = targetTier < performanceTier;
  const requiredStreak = isDowngrade ? 2 : 4;
  if (performanceTierCandidateStreak >= requiredStreak) {
    performanceTier = targetTier;
    performanceTierCandidate = performanceTier;
    performanceTierCandidateStreak = 0;
  }
}

function persistAudioSettings() {
  try {
    localStorage.setItem(AUDIO_SETTINGS_STORAGE_KEY, JSON.stringify({
      musicVolume,
      sfxVolume
    }));
  } catch {
    // Ignore quota/privacy mode errors.
  }
}

function persistViewDistanceSetting() {
  try {
    localStorage.setItem(VIEW_DISTANCE_SETTINGS_STORAGE_KEY, String(clampPreferredViewDistance(preferredViewDistance)));
  } catch {
    // Ignore quota/privacy mode errors.
  }
}

function persistTextureStyleSetting() {
  try {
    localStorage.setItem(TEXTURE_STYLE_STORAGE_KEY, normalizeTextureStyle(preferredTextureStyle));
  } catch {
    // Ignore quota/privacy mode errors.
  }
}

function formatVolumePercent(value) {
  return `${Math.round(clamp01(value) * 100)}%`;
}

function syncAudioSettingsUI() {
  if (musicVolumeSliderEl) musicVolumeSliderEl.value = `${Math.round(clamp01(musicVolume) * 100)}`;
  if (musicVolumeValueEl) musicVolumeValueEl.innerText = formatVolumePercent(musicVolume);
  if (sfxVolumeSliderEl) sfxVolumeSliderEl.value = `${Math.round(clamp01(sfxVolume) * 100)}`;
  if (sfxVolumeValueEl) sfxVolumeValueEl.innerText = formatVolumePercent(sfxVolume);
}

function syncViewDistanceUI() {
  const preferred = clampPreferredViewDistance(preferredViewDistance);
  if (viewDistanceSliderEl) viewDistanceSliderEl.value = `${preferred}`;
  if (viewDistanceValueEl) viewDistanceValueEl.innerText = `${preferred}`;
}

function getTextureStyleLabel(styleId) {
  const normalized = normalizeTextureStyle(styleId);
  if (normalized === TEXTURE_STYLE_CLASSIC) return t('menu.texture.classic');
  if (normalized === TEXTURE_STYLE_INDUSTRIAL) return t('menu.texture.industrial');
  if (normalized === TEXTURE_STYLE_ECO) return t('menu.texture.eco');
  if (normalized === TEXTURE_STYLE_BUJI) return t('menu.texture.buji');
  return t('menu.texture.classic');
}

function syncTextureStyleUI() {
  if (!textureStyleSelectEl) return;
  const classicOptionEl = textureStyleSelectEl.querySelector('option[value="classic"]');
  if (classicOptionEl) classicOptionEl.textContent = t('menu.texture.classic');
  const ecoOptionEl = textureStyleSelectEl.querySelector('option[value="eco"]');
  if (ecoOptionEl) ecoOptionEl.textContent = t('menu.texture.eco');
  const industrialOptionEl = textureStyleSelectEl.querySelector('option[value="industrial"]');
  if (industrialOptionEl) industrialOptionEl.textContent = t('menu.texture.industrial');
  const bujiOptionEl = textureStyleSelectEl.querySelector('option[value="buji"]');
  if (bujiOptionEl) bujiOptionEl.textContent = t('menu.texture.buji');
  textureStyleSelectEl.value = normalizeTextureStyle(preferredTextureStyle);
}

function setMusicVolume(value, { persist = true } = {}) {
  musicVolume = clamp01(value);
  bgmController.setMasterVolume(musicVolume);
  if (persist) persistAudioSettings();
  syncAudioSettingsUI();
}

function setSfxVolume(value, { persist = true } = {}) {
  sfxVolume = clamp01(value);
  sounds.setMasterVolume?.(sfxVolume);
  if (persist) persistAudioSettings();
  syncAudioSettingsUI();
}

function setPreferredViewDistance(value, { persist = true } = {}) {
  preferredViewDistance = clampPreferredViewDistance(value);
  if (persist) persistViewDistanceSetting();
  syncViewDistanceUI();
}

function setPreferredTextureStyle(value, { persist = true, toast = true } = {}) {
  preferredTextureStyle = normalizeTextureStyle(value);
  if (persist) persistTextureStyleSetting();
  syncTextureStyleUI();

  Promise.resolve(world.setTextureStyle?.(preferredTextureStyle))
    .then((state) => {
      const activeStyle = normalizeTextureStyle(state?.active || world.getTextureStyle?.() || preferredTextureStyle);
      if (!toast) return;
      showStatusByKey('status.texture_style_switched', { style: getTextureStyleLabel(activeStyle) }, 'level', 1.85);
    })
    .catch(() => {
      preferredTextureStyle = TEXTURE_STYLE_CLASSIC;
      syncTextureStyleUI();
      void world.setTextureStyle?.(TEXTURE_STYLE_CLASSIC);
      if (!toast) return;
      showStatusByKey('status.texture_style_switched', { style: getTextureStyleLabel(TEXTURE_STYLE_CLASSIC) }, 'level', 1.85);
    });
}

function formatBlendRow(label, blendMap, keys) {
  const shortKeyMap = {
    overworld: 'ovw',
    overworld_forest: 'for',
    overworld_tense: 'ten',
    nether: 'net',
    end: 'end',
    calm: 'cal',
    forest: 'for',
    tense: 'ten'
  };
  const body = keys
    .map((key) => `${shortKeyMap[key] || key.slice(0, 3)}:${(Number(blendMap?.[key] || 0)).toFixed(2)}`)
    .join(' | ');
  return `${label}: ${body}`;
}

function updateBgmDebugHud(delta = 0.016) {
  if (!bgmDebugEl || !bgmDebugVisible) return;

  bgmDebugHudSampleTimer = Math.max(0, bgmDebugHudSampleTimer - Math.max(0, delta));
  if (bgmDebugHudSampleTimer > 0) return;
  bgmDebugHudSampleTimer = 0.12;

  const snapshot = bgmController.getDebugSnapshot?.();
  if (!snapshot) {
    bgmDebugEl.innerText = 'Audio monitor unavailable';
    return;
  }

  const topCandidates = (snapshot.topCandidates || [])
    .slice(0, 3)
    .map((entry, index) => `${index + 1}. ${entry.name} (${entry.weight.toFixed(2)})`)
    .join(' | ');

  const trackLabel = snapshot.activeTrackName || 'silence';
  const styleLabel = snapshot.activeTrackStyle || '-';
  const volumeLabel = `${snapshot.currentVolume.toFixed(3)} / ${snapshot.targetVolume.toFixed(3)}`;
  const silenceLabel = Math.max(0, Number(snapshot.silenceTimer || 0)).toFixed(1);

  bgmDebugEl.innerText = [
    `BGM: ${trackLabel}`,
    `Style: ${styleLabel} | Vol: ${volumeLabel} | PauseScale: ${snapshot.pauseScale.toFixed(2)}`,
    `Pool: ${snapshot.dominantPoolLabel} (${snapshot.dominantPoolId}) | Silence: ${silenceLabel}s`,
    formatBlendRow('Dim', snapshot.dimensionBlend, ['overworld', 'nether', 'end']),
    formatBlendRow('Mood', snapshot.moodBlend, ['calm', 'forest', 'tense']),
    formatBlendRow('PoolMix', snapshot.poolContribution, ['overworld', 'overworld_forest', 'overworld_tense', 'nether', 'end']),
    `Top: ${topCandidates || '-'}`,
    `Context: ${bgmContext.dimension}/${bgmContext.biome || 'none'} threat:${Number(bgmContext.threat || 0).toFixed(2)}`
  ].join('\n');
}

function updatePerfHud(delta = 0.016, frameMs = 0, sectionMs = {}) {
  pushPerfSample(perfStats.frame, frameMs);
  pushPerfSample(perfStats.render, sectionMs.render || 0);
  pushPerfSample(perfStats.update, sectionMs.update || 0);
  pushPerfSample(perfStats.streaming, sectionMs.streaming || 0);
  pushPerfSample(perfStats.mobs, sectionMs.mobs || 0);
  pushPerfSample(perfStats.fluids, sectionMs.fluids || 0);
  pushPerfSample(perfStats.interaction, sectionMs.interaction || 0);

  if (!perfHudEl || !perfHudVisible) return;

  perfHudSampleTimer = Math.max(0, perfHudSampleTimer - Math.max(0, delta));
  if (perfHudSampleTimer > 0) return;
  perfHudSampleTimer = 0.2;

  const frameAvg = meanPerfSample(perfStats.frame);
  const frameP95 = percentilePerfSample(perfStats.frame, 0.95);
  const fpsAvg = frameAvg > 0.001 ? (1000 / frameAvg) : 0;
  const rendererInfo = renderer.info?.render || {};
  const worldPerf = world.getPerformanceSnapshot?.() || {};
  const mobPerf = mobSystem.getPerformanceSnapshot?.() || {};
  const raycastPerf = blockInteraction.getPerfSnapshot?.() || {};
  const schedulerPerf = frameTaskScheduler.getSnapshot?.() || {};
  const prefetch = worldPerf.prefetch || {};
  const intervals = getPerformanceIntervals();

  perfHudEl.innerText = [
    `Frame: avg ${frameAvg.toFixed(2)}ms | p95 ${frameP95.toFixed(2)}ms | fps ${fpsAvg.toFixed(1)}`,
    `Sections(ms): upd ${meanPerfSample(perfStats.update).toFixed(2)} | rnd ${meanPerfSample(perfStats.render).toFixed(2)} | str ${meanPerfSample(perfStats.streaming).toFixed(2)} | mob ${meanPerfSample(perfStats.mobs).toFixed(2)} | flu ${meanPerfSample(perfStats.fluids).toFixed(2)} | hit ${meanPerfSample(perfStats.interaction).toFixed(2)}`,
    `Renderer: calls ${Number(rendererInfo.calls || 0)} | tris ${Number(rendererInfo.triangles || 0)} | points ${Number(rendererInfo.points || 0)}`,
    `World: vd ${worldPerf.streamViewDistance || 0} | chunks ${worldPerf.visibleChunks || 0}/${worldPerf.loadedChunks || 0} | buildQ ${worldPerf.chunkBuildQueued || 0} | ray ${worldPerf.raycastObjects || 0} | inst ${worldPerf.instancedBlocks || 0}/${worldPerf.instancedBuckets || 0} | drops ${worldPerf.activeDrops || 0}/${worldPerf.activeDropUnits || 0}u (${worldPerf.sleepingDrops || 0} sleep, ${worldPerf.pooledDrops || 0} pooled) | saplings ${worldPerf.manualSaplings || 0}/${worldPerf.saplingsGrowing || 0} | light ${worldPerf.activePointLights || 0}/${worldPerf.deferredPointLights || 0}`,
    `Scene: leaves ${worldPerf.visibleLeafBuckets || 0}/${worldPerf.totalLeafBuckets || 0} buckets | leafInst ${worldPerf.visibleLeafInstances || 0}/${worldPerf.totalLeafInstances || 0} | fences ${worldPerf.visibleFenceMeshes || 0}/${worldPerf.totalFenceMeshes || 0}`,
    `Mobs: active ${mobPerf.activeMobs || 0} | tier ${mobPerf.perfTier ?? '-'} | LOS ${mobPerf.losUsed || 0}/${mobPerf.losBudget || 0} (left ${mobPerf.losBudgetLeft || 0}, skip ${mobPerf.losSkipped || 0}) | terrain ${mobPerf.terrainProbeBudgetLeft || 0} | hiMove ${mobPerf.precisionMoveBudgetLeft || 0}`,
    `FluidQ: water ${worldPerf.dirtyWaterCells || 0} | lava ${worldPerf.dirtyLavaCells || 0} | seedW ${worldPerf.pendingWaterSeedCells || 0} | seedL ${worldPerf.pendingLavaSeedCells || 0} | tier ${worldPerf.fluidTier ?? '-'} | focusR ${worldPerf.fluidFocusRadius || 0} | budget ${worldPerf.fluidWaterSeedBudget || 0}/${worldPerf.fluidLavaSeedBudget || 0} | vis ${worldPerf.fluidWaterVisualProcessed || 0}/${worldPerf.fluidWaterVisualBudget || 0} (+${worldPerf.fluidWaterVisualDeferred || 0}) | anim ${worldPerf.waterAnimatingMeshes || 0} | culled ${worldPerf.culledBlocks || 0}`,
    `Raycast: queries ${raycastPerf.raycastCalls || 0} | cache ${raycastPerf.raycastCacheHits || 0} | voxel ${raycastPerf.voxelRaycastCalls || 0} | dynamic ${raycastPerf.dynamicRaycastCalls || 0}`,
    `Scheduler: budget ${Number(schedulerPerf.budgetMs || 0).toFixed(2)}ms | spent ${Number(schedulerPerf.spentMs || 0).toFixed(2)}ms | run ${schedulerPerf.runs || 0}/${schedulerPerf.queued || 0} | skip ${schedulerPerf.skips || 0}`,
    `Governor: ${PERFORMANCE_TIER_LABELS[performanceTier] || 'Balanced'} | mob ${Math.round(intervals.mob * 1000)}ms | fluid ${Math.round(intervals.fluid * 1000)}ms | drop ${Math.round(intervals.drops * 1000)}ms`,
    `Prefetch: ${prefetch.enabled ? 'on' : 'off'} | cache ${prefetch.cachedChunks || 0} | q ${prefetch.queuedChunks || 0} | in-flight ${prefetch.inflightChunks || 0} | hit ${prefetch.hits || 0}/${(prefetch.hits || 0) + (prefetch.misses || 0)}`
  ].join('\n');
}

setBgmDebugVisible(bgmDebugVisible, { persist: false, toast: false });
setPerfHudVisible(perfHudVisible, { persist: false, toast: false });
syncAudioSettingsUI();
syncViewDistanceUI();
syncTextureStyleUI();
setPreferredTextureStyle(preferredTextureStyle, { persist: false, toast: false });

function animate() {
  requestAnimationFrame(animate);

  const time = performance.now();
  const timeSeconds = time * 0.001;
  const delta = Math.min((time - prevTime) / 1000, 0.05);
  const currentDimension = getCurrentDimension();
  const uiWindowOpen = isUiWindowOpen();
  const frameStartMs = performance.now();
  const sectionMs = {
    update: 0,
    render: 0,
    streaming: 0,
    mobs: 0,
    fluids: 0,
    interaction: 0
  };

  blockInteraction.beginFrame(time);
  updatePerformanceGovernor(delta);

  updateDynamicResolution(delta);
  updateStatusToast(delta);
  deathFeedbackSystem?.updateDamageFeedbackOverlay(delta);
  const bgmPaused = pauseMenuVisible || isPlayerDeathAnimating() || uiWindowOpen;
  bgmController.setContext({
    dimension: bgmContext.dimension,
    biome: bgmContext.biome,
    threat: bgmContext.threat,
    paused: bgmPaused
  });
  bgmController.update(delta);
  updateCoordinatesHud(delta);
  deathRecovery.updateDeathRecoveryMarker(delta, timeSeconds, currentDimension);
  deathRecovery.updateDeathRecoveryHud(delta);
  updateBgmDebugHud(delta);
  particles.updateDebrisParticles(delta);
  particles.updateCactusPulseShells(delta);
  playerCombatFluidSystem.updateShake(delta);

  if (pauseMenuVisible || uiWindowOpen) {
    mobUpdateAccumulator = 0;
    fluidUpdateAccumulator = 0;
    dropUpdateAccumulator = 0;
    interactionRefreshAccumulator = 0;
    if (furnaceSystem?.isOpen()) {
      furnaceSystem.update(Math.max(0.016, delta));
      frameTaskScheduler.touchTask?.('furnace-system');
    }
    if (uiWindowOpen) {
      player.resetInput();
      if (controls.isLocked || document.pointerLockElement) forcePointerUnlock('hide');
      blockInteraction.clearPreview();
    }
    const renderStartMs = performance.now();
    renderer.render(scene, camera);
    sectionMs.render = performance.now() - renderStartMs;
    sectionMs.update = (performance.now() - frameStartMs) - sectionMs.render;
    updatePerfHud(delta, performance.now() - frameStartMs, sectionMs);
    prevTime = time;
    return;
  }

  if (isPlayerDeathAnimating()) {
    mobUpdateAccumulator = 0;
    fluidUpdateAccumulator = 0;
    dropUpdateAccumulator = 0;
    interactionRefreshAccumulator = 0;
    deathFeedbackSystem?.updateDeathAnimation(delta);
    const renderStartMs = performance.now();
    renderer.render(scene, camera);
    sectionMs.render = performance.now() - renderStartMs;
    sectionMs.update = (performance.now() - frameStartMs) - sectionMs.render;
    updatePerfHud(delta, performance.now() - frameStartMs, sectionMs);
    prevTime = time;
    return;
  }
  deathFeedbackSystem?.resetDeathCameraRoll?.();

  meleeCooldown = Math.max(0, meleeCooldown - delta);
  rangedCooldown = Math.max(0, rangedCooldown - delta);
  creativeHoldMineCooldown = Math.max(0, creativeHoldMineCooldown - delta);
  portalCooldownTime = Math.max(0, portalCooldownTime - delta);
  if (isBlockingWithShield) {
    const activeId = ui.getActiveItemId();
    if (!controls.isLocked || isUiWindowOpen() || activeId !== 'shield' || ui.getActiveItemCount() <= 0) {
      stopShieldBlocking();
    }
  }
  if (bowCharging) {
    const activeId = ui.getActiveItemId();
    if (!controls.isLocked || isUiWindowOpen() || activeId !== 'bow' || ui.getActiveItemCount() <= 0) {
      cancelBowCharge();
    }
  }
  dayNight.update(delta);
  world.updateFire(delta, timeSeconds);
  bgmSampleTimer = Math.max(0, bgmSampleTimer - delta);
  refreshBgmContext(false, currentDimension);
  if (currentDimension === 'nether') {
    scene.background.setHex(0x4b1a16);
    scene.fog.color.setHex(0x4b1a16);
    scene.fog.density = 0.02;
    hemi.intensity = 0.14;
    ambientLight.intensity = 0.12;
    dirLight.intensity = 0.16;
  } else if (currentDimension === 'end') {
    scene.background.setHex(0x100a20);
    scene.fog.color.setHex(0x100a20);
    scene.fog.density = 0.022;
    hemi.intensity = 0.1;
    ambientLight.intensity = 0.1;
    dirLight.intensity = 0.2;
  }
  const schedulerBudgetMs = performanceTier <= 0
    ? (delta > 0.024 ? 0.45 : 0.85)
    : (performanceTier >= 2
      ? (delta > 0.024 ? 1.1 : 2.6)
      : (delta > 0.024 ? 0.8 : (delta > 0.019 ? 1.35 : 2.2)));
  frameTaskScheduler.runFrame(delta, {
    budgetMs: schedulerBudgetMs
  });
  heldItem.update(time, delta);
  updateMiningOverlay(delta);
  playerInteractionSystem.update(delta);

  if (isUiWindowOpen()) {
    if (controls.isLocked || document.pointerLockElement) forcePointerUnlock('hide');

    blockInteraction.clearPreview();
  }

  if (ui.isCraftOpen()) {

    tableAccessRefreshTimer -= delta;
    if (tableAccessRefreshTimer <= 0) {
      ui.setTableAccess(hasTableAccess());
      tableAccessRefreshTimer = 0.22;
    }
  }

  if (controls.isLocked) {
    const streamingStartMs = performance.now();
    world.updateStreaming(player.getPosition(), delta, getAdaptiveStreamingBuildBudget(delta), {
      viewDistance: getAdaptiveStreamingViewDistance()
    });
    sectionMs.streaming += performance.now() - streamingStartMs;
    const portalId = getPlayerPortalBlockId();
    if (portalId && portalCooldownTime <= 0) {
      portalChargeTime += delta;
      if (portalChargeTime >= PORTAL_TRAVEL_CHARGE_SECONDS) {
        if (portalId === NETHER_PORTAL_BLOCK_ID) tryTravelThroughNetherPortal();
        else if (portalId === END_PORTAL_BLOCK_ID) tryTravelThroughEndPortal();
      }
    } else {
      portalChargeTime = Math.max(0, portalChargeTime - delta * 3.2);
    }

    if (isMining && miningTarget) {
      const playerPos = player.getPosition();
      const dx = playerPos.x - miningTarget.position.x;
      const dy = playerPos.y - miningTarget.position.y;
      const dz = playerPos.z - miningTarget.position.z;
      const mineDistSq = (dx * dx) + (dy * dy) + (dz * dz);
      const currentAimHit = blockInteraction.getTargetHit(6.0, { force: false });
      const stillAimingTarget = !!(currentAimHit && isSameBlockTarget(currentAimHit.object, miningTarget));
      const raycastReady = world.hasRaycastObject(miningTarget);
      const creativeMining = isCreativeMode();
      const aimLostGrace = creativeMining ? 0.085 : 0.05;

      if (mineDistSq > 40.96) {
        stopMining();
      } else if (!stillAimingTarget || !raycastReady) {
        miningAimLostTimer += delta;
        if (miningAimLostTimer >= aimLostGrace) stopMining();
      } else {
        miningAimLostTimer = 0;
        const activeItemId = ui.getActiveItemId();
        const effectiveItem = ui.getActiveItemCount() > 0 ? activeItemId : null;
        const speed = getMiningSpeed(effectiveItem, miningTarget.userData.id);
        const reqTime = creativeMining
          ? 0.025
          : Math.max(0.06, (miningTarget.userData.hardness || 0.4) / speed);
        miningRequiredTime = reqTime;
        miningProgress += delta;
        updateMineUI((miningProgress / reqTime) * 100);

        const hitIntervalMs = THREE.MathUtils.clamp(reqTime * 360, 80, 220);
        if (time - lastHitSoundTime > hitIntervalMs) {
          sounds.hitByBlockId(miningTarget.userData.id);
          heldItem.triggerSwing(0.78);
          miningPulse = 1.0;
          lastHitSoundTime = time;
        }

        if (miningProgress >= reqTime) {
          const blockId = miningTarget.userData.id;
          const breakX = miningTarget.position.x;
          const breakY = miningTarget.position.y;
          const breakZ = miningTarget.position.z;
          camera.getWorldDirection(miningLookDir);
          const lookingDown = miningLookDir.y < -0.48;
          sounds.break(blockId);
          heldItem.triggerSwing(1.0);
          particles.spawnBlockDebrisBurst(breakX, breakY + 0.08, breakZ, {
            blockId,
            count: lookingDown
              ? (creativeMining ? 18 : 14)
              : (creativeMining ? 10 : 8),
            spread: lookingDown ? 0.5 : 0.34,
            speed: lookingDown ? 3.0 : 2.45,
            upward: lookingDown ? 2.35 : 2.8,
            lifeMin: 0.2,
            lifeMax: 0.52
          });

          const handledByFarming = creativeMining
            ? !!farmingSystem?.isCropBlockId(blockId)
            : !!farmingSystem?.handleCropBreakByMining(miningTarget);
          if (!creativeMining && !handledByFarming) {
            if (blockId === 4) {
              const isUsingShears = ITEM_DEFS[effectiveItem]?.toolType === 'shears';
              if (isUsingShears) {
                spawnItemDrop(miningTarget.position.x, miningTarget.position.y, miningTarget.position.z, '4', 1);
                experienceSystem?.onMineBlock(blockId);
              }
            } else {
              const dropItemId = getDropItemId(blockId);
              let resolvedDropId = dropItemId;
              if (blockId === 27 && dropItemId && Math.random() < 0.12) {
                resolvedDropId = 'flint';
              }
              const canHarvest = !!(resolvedDropId && canHarvestDrop(effectiveItem, blockId));
              if (canHarvest) {
                if (String(resolvedDropId) === String(blockId)) {
                  world.spawnDrop(miningTarget.position.x, miningTarget.position.y, miningTarget.position.z, blockId);
                } else {
                  spawnItemDrop(miningTarget.position.x, miningTarget.position.y, miningTarget.position.z, resolvedDropId, 1);
                }
                if (blockId === 1 && Math.random() < 0.22) {
                  spawnItemDrop(miningTarget.position.x, miningTarget.position.y, miningTarget.position.z, 'wheat_seed', 1);
                }
                experienceSystem?.onMineBlock(blockId);
              }
            }
          }

          if (blockId === 13) {
            dropFurnaceContentsAt(miningTarget.position.x, miningTarget.position.y, miningTarget.position.z);
          }
          if (blockId === 14) {
            dropChestContentsAt(miningTarget.position.x, miningTarget.position.y, miningTarget.position.z);
          }

          world.removeBlock(miningTarget, { trackModification: true });
          farmingSystem?.onBlockRemoved(
            Math.round(miningTarget.position.x),
            Math.round(miningTarget.position.y),
            Math.round(miningTarget.position.z),
            blockId
          );
          cleanupUnsupportedFixturesAround(
            Math.round(miningTarget.position.x),
            Math.round(miningTarget.position.y),
            Math.round(miningTarget.position.z)
          );
          world.settleGravityAround(
            Math.round(miningTarget.position.x),
            Math.round(miningTarget.position.y),
            Math.round(miningTarget.position.z),
            1
          );
          objectiveSystem?.onMineBlock(blockId, 1);
          const def = ITEM_DEFS[activeItemId];
          if (!creativeMining && def && def.kind === 'tool' && speed > 0.5) ui.damageActiveTool(1);
          if (creativeMining) creativeHoldMineCooldown = CREATIVE_HOLD_MINE_COOLDOWN;
          stopMining();
        }
      }
    }

    const playerPosBeforeUpdate = player.getPosition();
    playerPrevPos.copy(playerPosBeforeUpdate);
    player.update(delta);
    boatSystem?.update?.(delta);
    const playerPosAfterUpdate = player.getPosition();
    const playerFeetY = player.getFeetY();
    const playerHeight = player.getHeight();
    playerCurrPos.copy(playerPosAfterUpdate);

    const movedDistance = playerPrevPos.distanceTo(playerCurrPos);
    survival.update(
      delta,
      movedDistance,
      isMining,
      !!player.isHeadInWater?.(),
      !!player.isInLava?.(),
      !!player.isHeadInLava?.(),
      !!player.isInFire?.(),
      !!player.isInWater?.()
    );
    const cactusSource = getTouchingCactusSourcePosition(playerPosAfterUpdate, playerFeetY, playerHeight);
    if (cactusSource) {
      survival.applyDamage(1, 'cactus', { sourcePosition: cactusSource });
    }
    
    if (fireOverlayEl && survival.getOnFireDuration) {
      fireOverlayEl.style.opacity = survival.getOnFireDuration() > 0 ? 1 : 0;
    }
    camera.getWorldDirection(worldVisibilityDir);
    world.updateChunkVisibility(playerPosAfterUpdate, worldVisibilityDir, delta);

    const intervals = getPerformanceIntervals();
    dropUpdateAccumulator = Math.min(0.2, dropUpdateAccumulator + delta);
    if (dropUpdateAccumulator >= intervals.drops) {
      const dropDelta = Math.min(0.08, dropUpdateAccumulator);
      dropUpdateAccumulator = 0;
      world.updateCacti(dropDelta);
      world.updateDrops(time, dropDelta, playerPosAfterUpdate, (drop) => {
        const dropCount = Math.max(1, Math.floor(Number(drop?.count) || 1));
        if (drop?.kind === 'xp') {
          const xpValue = Math.max(0, Number(drop.id) || 0);
          if (xpValue <= 0) return false;
          experienceSystem?.addExperience(xpValue * dropCount);
          sounds.pickup();
          return dropCount;
        }

        const dropItemId = drop?.kind === 'block' ? getDropItemId(drop.id) : drop?.id;
        if (!dropItemId) return false;

        const insertCapacity = typeof ui.getInsertCapacity === 'function'
          ? Math.max(0, Math.floor(Number(ui.getInsertCapacity(dropItemId)) || 0))
          : dropCount;
        const acceptedCount = Math.max(0, Math.min(dropCount, insertCapacity));
        if (acceptedCount <= 0) return 0;

        const picked = ui.addItem(dropItemId, acceptedCount);
        if (!picked) return 0;

        sounds.pickup();
        deathRecovery.onDeathRecoveryDropPicked(drop?.source, acceptedCount);
        objectiveSystem?.onCollectItem(dropItemId, acceptedCount);
        ui.updateInventoryUI();
        if (ui.isCraftOpen()) ui.setTableAccess(hasTableAccess());
        return acceptedCount;
      });
    }

    interactionRefreshAccumulator = Math.min(0.15, interactionRefreshAccumulator + delta);
    if (interactionRefreshAccumulator >= intervals.interaction) {
      interactionRefreshAccumulator = 0;
      const interactionStartMs = performance.now();
      blockInteraction.refreshPlacementPreview(false);
      blockInteraction.refreshTargetHighlight(false);
      sectionMs.interaction += performance.now() - interactionStartMs;
    }
  }

  const nightState = dayNight.getNightState();
  const intervals = getPerformanceIntervals();

  mobUpdateAccumulator = Math.min(0.2, mobUpdateAccumulator + delta);
  if (mobUpdateAccumulator >= intervals.mob) {
    const simDelta = Math.min(0.08, mobUpdateAccumulator) * playerCombatFluidSystem.getHitStopScale();
    mobUpdateAccumulator = 0;
    const mobStartMs = performance.now();
    mobSystem.update(
      simDelta,
      nightState.isNight,
      nightState.nightStrength,
      currentDimension,
      dayNight.getDayCount()
    );
    sectionMs.mobs += performance.now() - mobStartMs;
  }

  fluidUpdateAccumulator = Math.min(0.2, fluidUpdateAccumulator + delta);
  if (fluidUpdateAccumulator >= intervals.fluid) {
    const simDelta = Math.min(0.08, fluidUpdateAccumulator);
    fluidUpdateAccumulator = 0;
    const fluidStartMs = performance.now();
    world.updateWaterFlow(simDelta, getFluidUpdateOptions(simDelta));
    sectionMs.fluids += performance.now() - fluidStartMs;
  }

  prevTime = time;
  const renderStartMs = performance.now();
  renderer.render(scene, camera);
  sectionMs.render = performance.now() - renderStartMs;
  sectionMs.update = (performance.now() - frameStartMs) - sectionMs.render;
  updatePerfHud(delta, performance.now() - frameStartMs, sectionMs);
}

window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setPixelRatio(dynamicPixelRatio);
  renderer.setSize(window.innerWidth, window.innerHeight, false);
});

animate();
